import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useChatContext } from '../ChatContext';
import { API_BASE } from '../config';
import WingmanLogo from '../components/WingmanLogo';
import ConversationDeckProposalCard from '../components/authored-decks/ConversationDeckProposalCard';
import { DeckBuilderEntry } from '../components/deck-builder/DeckBuilderModal';
import ChatCaptureConsentBanner from '../components/ChatCaptureConsentBanner';
import CaptureChip from '../components/CaptureChip';
import CaptureEditModal from '../components/CaptureEditModal';
import ConversationDeckSupplierAsk, { SUPPLIER_ASK_CODE, deckSupplierReply } from '../components/authored-decks/ConversationDeckSupplierAsk';
import ChatMarkdown from '../components/ChatMarkdown';
import ChatAnswer from '../components/ChatAnswer';
import ChatActionButtons from '../components/ChatActionButtons';
import { placeOfferOnAnswer, showsAnswerOffer } from '../utils/chatActionOffer';
import { useToast } from '../components/ToastHost';
import { createDispatcher } from '../utils/captureDispatcher';
import { formatChipLabel } from '../utils/captureLabel';
import {
  shouldRenderChip,
  getProvenanceRationale,
  getProvenanceDeepLink,
  getProvenanceLabel,
} from '../utils/provenanceChip';
import { hasConsent } from '../utils/captureConsent';
import { buildChatHistory } from '../utils/chatHistory';
import { evaluateBrief, retractBriefEntry } from '../api/negotiationBrief';
import { confirmCandidate, dismissCandidate, retractCandidate } from '../api/chatCapture';
import { chatErrorMessage, GENERIC_MESSAGE } from '../utils/chatErrorMessage';
import { requestChatTurn } from '../utils/chatProgressStream';
import { mirrorStreamReduce } from '../utils/mirrorAnswerStream';
import useChatAutoScroll from '../hooks/useChatAutoScroll';
import ChatLiveProgress from '../components/ChatLiveProgress';
import { uploadDocumentForEvaluation } from '../api/docUpload';
import { attachSupplierToCandidate } from '../api/noticeAttach';
import NoticeSupplierConfirm from '../components/NoticeSupplierConfirm';
import SourcingEventOfferAction from './sourcing/SourcingEventOfferAction';
import SourcingIntakeCard, { SourcingIntakeBanner, SourcingIntakeBuilt } from './sourcing/SourcingIntakeCard';
import SourcingIntakeCardV2, { IntakeChangeBar, IntakeDraftBar, intakeChangePlaceholder,
  liveIntakeCardIndex, newestIntakeCards } from './sourcing/SourcingIntakeCardV2';
import { viewerZone } from './sourcing/useSourcingChat';
import { ReportOfferAction, ReportPlanCard, ReportJobRow,
         ReportJobRehydrator, ReportStartRetry,
         projectReportResult, WorkbookChoiceBar,
         WorkbookChoiceBoundary } from '../components/ReportPlanCard';
import ChatFileChips from '../components/ChatFileChips';
import { useWorkbookChoice } from '../hooks/useWorkbookChoice';
// Lane 690 — the decision half of "the chat turn waits for the letter" lives in
// its own pure module so the rule can be red-tested without mounting this page.
import {
  WAIT,
  READ_DEADLINE_MS,
  CONFIRM_DEADLINE_MS,
  POLL_INTERVAL_MS,
  SKIP_LABEL,
  aggregateWait,
  describeWaiting,
  describeUndelivered,
  slotDelivered,
  turnCandidateIds,
  turnFailedUploads,
} from '../utils/chatDocumentWait';
import {
  ATTACH_OUTCOME,
  describeAttachHttpError,
  describeAttachNetworkError,
  describeAttachOutcome,
} from '../utils/noticeAttachStatus';
import { CONFIRM_COPY } from '../utils/noticeConfirmCopy';
import { keepNotice } from '../utils/chatProgressNotice';
import {
  acceptAttrFor,
  DUPLICATE_ACTION,
  OUTCOME,
  describeHttpError,
  describeNetworkError,
  describeOutcome,
  describeRereading,
  duplicateAction,
  preflight,
} from '../utils/docUploadStatus';
import './Home.linear.css';

/* ══ THE INTELLIGENCE DOOR (Lane 861) ═══════════════════════════════════════
   Ryan, after QA on staging: *"we need a place for them to see their chat
   captures, like, how they find it. What's a clickable link? Where should we
   put it?"* — and, hunting for the page he himself commissioned, *"I need to
   figure out where that captures."*

   A nav link ALREADY EXISTED (Sidebar's "Intelligence" item). He still could
   not find it, for two reasons this block addresses:

     1. THE THING HAD THREE NAMES — nav said "Intelligence", the URL says
        `/captures`, the feature is called "chat capture". Every string here
        says ONE word, and it is the nav's word.
     2. NOTHING CONNECTED THE MOMENT OF CAPTURE TO WHERE CAPTURES LIVE. The
        chip under a chat bubble was the only signal, it is tied to one message,
        and it scrolls away. So the door goes where captures are MADE — the
        bottom of the chat — where it is persistent and belongs to no one turn.

   ⚠️ AND IT GOES *ONLY* THERE. A second affordance on the CaptureChip itself was
   built and then CUT by Ryan on review of the render: *"we don't need the open
   intelligence button there... on the chip piece. The chip is fine. They don't
   need the open intelligence button in there. There's an intelligence button on
   the nav bar. There's an intelligence button at the bottom of the chat."* Two
   doors already exist — the nav item and this one — and a third on a control
   whose whole job is Confirm / Edit / Dismiss is clutter. `CaptureChip.js` is
   byte-identical to what it was before this lane, deliberately. Do not
   re-add it.

   ⚠️ ROUTE TARGET IS `/intelligence`, NOT `/captures`, AND THE DIFFERENCE IS
   THE WHOLE POINT OF THE LINK.
   Both paths render the same component (App.js). `/intelligence` is, in App.js's
   own words, "the real home and the one the nav links to" — and Sidebar's
   active-item match is `pathname.startsWith(item.path)`. So arriving via
   `/intelligence` LIGHTS UP the Intelligence nav item, and arriving via
   `/captures` does not. Ryan's complaint was that he could not find this page
   at all; a link that delivers him to the right page with the nav item unlit
   answers the immediate question and teaches him nothing about where the page
   permanently lives. The highlight IS the teaching moment, and it is most of
   why item 1 exists.

   The obvious counter — `/captures` is registered unconditionally, so it can
   never dead-end — does not apply HERE. `/intelligence` is registered on
   `intelligencePortalEnabled` and THIS LINK IS GATED ON THE SAME FLAG, read in
   the same render pass, so the link can only exist in exactly the state where
   its target route exists. There is no reachable state where it points at an
   unregistered route.

   ⚠️ `/captures` IS UNTOUCHED AND MUST STAY THAT WAY. It is the target of the
   provenance deep link built in `utils/provenanceChip.js`
   (`/captures?candidate_id=X`) and asserted by `Home.provenance.test.js` — a
   live contract that predates this lane. This lane changed ONE link's
   destination, not the routing.

   ⚠️ THE GATE IS NOT OPTIONAL. `INTELLIGENCE_PORTAL_ENABLED` defaults OFF and
   is absent on PROD, and every `backend/routes/intelligence.py` endpoint 404s
   when it is off. An ungated link would ship a button on production pointing at
   a dead page. The gate is the SAME `/me` echo the sidebar item carries
   (`intelligencePortalEnabled`) — not a new flag, not a wider one — so the door
   tracks the room in every environment, whatever the flags happen to be. This
   is the established shape on this page (see `templateDecksEnabled` at the
   "Prepare for a meeting" intent). Pinned by Home.intelligenceDoor.test.js. */
const INTELLIGENCE_COPY = Object.freeze({
  // Ryan's own wording, kept verbatim. Plain language rather than the product
  // noun: it describes what is behind the door, which is what he was looking
  // for when he could not name the page.
  SEE_ALL: 'See everything Wingman has recorded →',
});

/* ══ THERE IS NO CAPTURE DISCLOSURE ON THIS PAGE — Ryan, D9, 2026-08-05 ══

   Lane 863 shipped a standing disclosure line here as build-plan item D-2, to
   stand in for the consent banner once `ALWAYS_ON_CAPTURE_ENABLED` retires it.
   ⛔ D-2 IS CANCELLED. Ryan: *"I don't want any disclosure on the chat page. So
   it does... nothing needs to be there."*

   So the flag now means exactly one thing on this page: THE BANNER GOES AWAY,
   and nothing takes its place. D1 / D2 / D3 are untouched — capture still stops
   asking permission when the flag is on.

   ⚠️ DO NOT RE-ADD A REPLACEMENT LINE. It was built, reviewed, rendered and cut
   by Ryan; `Home.alwaysOnCapture.test.js` carries a decision pin that fails if
   anything returns to that footer slot. If a future lane believes a disclosure
   is required, that is a conversation with Ryan, not a re-implementation.

   Lane 861's footer link — `INTELLIGENCE_COPY.SEE_ALL`, above — is a DIFFERENT
   element, signed by Ryan verbatim, live on PROD, and STAYS. It is where a user
   goes to see and remove what has been recorded. */

function Home() {
  const navigate = useNavigate();
  const {
    token, user, chatCaptureEnabled, opportunitiesEnabled, templateDecksEnabled,
    // Lane 616 (Phase 5): `docUploadEnabled` is the per-company kill switch
    // echo; `authFetch` is used ONLY by the upload call, because this page's
    // own `getAuthHeaders()` hardcodes `Content-Type: application/json`, which
    // would destroy a multipart body's boundary. The chat pipeline keeps using
    // `getAuthHeaders()` exactly as before.
    docUploadEnabled, authFetch,
    // Lane 763 (Phase 1b): the per-company kill switch for the negotiation
    // brief. Deliberately NOT chatCaptureEnabled — see AuthContext.
    negotiationBriefEnabled,
    // Lane 690: the dial that makes a chat turn WAIT for a document attached to
    // the same Send. OFF => `handleChatSubmit` takes its pre-Lane-690 branch.
    chatAwaitsDocumentEnabled,
    // Lane 1400 WS1: honest uploads. OFF => the upload request, the turn
    // body and the rendered answer are byte-identical to before the lane.
    chatUploadHonestyEnabled,
    // Lane 714 (D50.5): the dial that makes a duplicate re-read ITSELF instead
    // of parking behind a mandatory "Read it again anyway" click. OFF => the
    // duplicate card is byte-identical to today, button and all.
    duplicateNonblockingEnabled,
    // Lane 861: the /me echo of INTELLIGENCE_PORTAL_ENABLED. It gates BOTH new
    // doors on this page — the persistent footer link and the chip's see-all
    // affordance. See INTELLIGENCE_DOOR below for why it is not optional.
    intelligencePortalEnabled,
    // Lane 863: the /me echo of ALWAYS_ON_CAPTURE_ENABLED — the retirement of
    // the per-USER consent gate. OFF (the default, and the PROD posture) =>
    // this page is byte-identical to before that lane: the consent banner
    // renders, and both extractors consult `hasConsent`. ON => the banner is
    // gone and NOTHING replaces it (Ryan, D9 — see the block above).
    alwaysOnCaptureEnabled,
    // Lane 1132 (Plan A): the /me echo of the EFFECTIVE workbook creation
    // gate. OFF => the offer/plan/job surfaces never render and this page
    // is byte-identical to before the lane (the server also withholds
    // report_offer, so this is the double lock, same shape as deckIntent).
    workbookExportEnabled,
    sourcingEventsEnabled,
    // Lane 1368: with the chat intake on, the one-click event offer is never shown
    // (decision D2; the server stops making it too).
    sourcingChatIntakeEnabled,
    // Lane 1439: the redesigned RFP card (only with the chat intake on).
    sourcingIntakeCardV2Enabled,
    // Lane 1211: chat-first landing gate (see AuthContext). ON => the new
    // empty-state (guidance + one example + full-guide link, no buttons) and
    // a calm composer focus; OFF => the current landing, byte-identical.
    homeChatFirstLandingEnabled,
    // Lane 1400 WS3: the composer also accepts a .csv (stored as a table).
    // OFF => the accept list and the type check are byte-identical.
    uploadTablesEnabled,
    // Lane 1405 v3: the /me echo of the deck builder (the Build a deck button).
    deckBuilderEnabled,
  } = useAuth();
  const { chatMessages, setChatMessages, conversationId, startNewChat, liveConversationRef } = useChatContext();
  // Lane 1439: the RFP card, version 2. `intakeChange` is the change box in the chat
  // box ("Make changes"): null when closed, else { intakeId, topic } (topic null = the
  // four choices). Always null with version 2 off.
  const intakeV2 = sourcingChatIntakeEnabled && sourcingIntakeCardV2Enabled;
  // Lane 861's door to /intelligence (see the comment where it renders).
  const intelDoor = (
    <div className={`hm-inteldoor${intakeV2 ? ' hm-inteldoor--below' : ''}`} data-testid="hm-intel-door">
      <button
        type="button"
        className="hm-link"
        data-testid="hm-intel-link"
        onClick={() => navigate('/intelligence')}
      >
        {INTELLIGENCE_COPY.SEE_ALL}
      </button>
    </div>
  );
  const [intakeChange, setIntakeChange] = useState(null);
  // Lane 1398 Phase B: after a workbook is built, typing locks until the
  // buyer picks Change this workbook / Ask about something else.
  const workbookChoice = useWorkbookChoice(conversationId);
  const { showToast } = useToast();
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const inputRef = useRef(null);
  // Lane 1368 (S6): "Start in chat" on the Sourcing events page opens Home with the
  // composer filled (never sent). Read only with the chat intake on; cleared from the
  // history entry so a reload does not fill it again.
  const location = useLocation();
  const composerDraft = sourcingChatIntakeEnabled ? location.state?.composerDraft : null;
  useEffect(() => {
    if (typeof composerDraft !== 'string' || !composerDraft) return;
    setChatInput((current) => current || composerDraft);
    navigate(location.pathname, { replace: true, state: null });
    window.requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
    });
  }, [composerDraft, navigate, location.pathname]);
  // Lane 709: the live transcript length, for `runAttach` to stamp onto the
  // chat-footer door. A callback fired from a click reads the render it was
  // created in; the ref reads NOW, which is the value that decides whether a
  // later assistant message counts as "after the confirm".
  const chatMessagesRef = useRef(chatMessages);
  chatMessagesRef.current = chatMessages;
  // Lane 1439: the context's live ref (the conversation current now, kept by
  // ChatContext), so a Home that unmounted mid-turn never writes into a newer chat.
  // It always equals conversationId while that id does not change.
  const conversationRef = liveConversationRef;
  // ── Lane 690: the turn waits for the letter ───────────────────────────────
  // `chatWaitLine` is the sentence that replaces "Analyzing..." while a turn is
  // held for an attached document. Non-null is also what renders the escape
  // control, so the two can never disagree about whether a wait is in progress.
  //
  // The skip is a REF, not state: it is read inside a running poll loop, and a
  // state value captured in that closure would never update. It is armed by the
  // "Answer now" button and disarmed at the start of every wait.
  const [chatWaitLine, setChatWaitLine] = useState(null);
  const skipDocWaitRef = useRef(false);

  // ── Lane 1082 (WB2): live progress for the turn itself ────────────────────
  // `chatProgress` drives the current activity while /api/ai-assistant/stream
  // narrates the turn: { startedAt, line, sourcesLine }. It is a SEPARATE state
  // from `chatWaitLine` on purpose — chatWaitLine's non-null also renders the
  // document-wait escape control, which a normal turn must never show. Every
  // string rendered from it is server code-owned copy, never model prose.
  // ChatLiveProgress owns its elapsed timer without re-rendering this page.
  const [chatProgress, setChatProgress] = useState(null);
  // Mirror Mode (M1, Lane 1103): the live answer draft streamed token by
  // token via answer_delta/answer_reset events. Reducer logic lives in
  // utils/mirrorAnswerStream (Home.js composes, does not own it). The draft
  // is a preview only — the final bubble always renders the payload's
  // `answer`, and the draft dies with the turn in the finally below.
  const [chatStreamDraft, setChatStreamDraft] = useState(null);
  // Lane 1405 v3 Part B: the answer layout version the server announced for
  // the running turn (`answer_layout` stream event), or null (legacy draft).
  const [chatStreamLayout, setChatStreamLayout] = useState(null);
  // Lane 1405 v3: offers whose workbook is starting from the Build a workbook
  // button; that button stays disabled meanwhile (one start per offer).
  const [startingOffers, setStartingOffers] = useState([]);
  const { scrollRef, contentRef, showJump, jumpToLatest } = useChatAutoScroll({
    conversationId, active: chatLoading,
  });

  // Chat-capture dispatcher owns the async evaluate lifecycle. One per Home
  // mount, constructed in an effect so React 18 StrictMode's double-invoke
  // doesn't leak a torn-down dispatcher into ref.current.
  const dispatcherRef = useRef(null);
  useEffect(() => {
    dispatcherRef.current = createDispatcher();
    return () => {
      dispatcherRef.current?.destroy?.();
      dispatcherRef.current = null;
    };
  }, []);

  // Chip state keyed by user messageId. Local to Home (not ChatContext) —
  // chips are transient UI enrichments; D.5b's My Captures is the persistent
  // surface for unfinished pending captures.
  const [chipStatesByMessage, setChipStatesByMessage] = useState(() => new Map());
  // ══ Lane 773 / Phase 1c — WHAT THE BRIEF JUST RECORDED, AND AGAINST WHOM ══
  //
  // Keyed by the SAME user messageId `chipStatesByMessage` uses, and for the
  // same reason: this is an async classifier verdict about THAT message, and
  // keying it prevents the result ever binding to a different bubble no matter
  // how the transcript grew while it was in flight.
  //
  // ⚠️ IT IS A SIDECAR MAP AND NOT A FIELD ON THE MESSAGE, WHICH IS LOAD-BEARING.
  // `sendChatMessage` builds `historyForBackend` from `chatMessages` `m.content`,
  // so anything written into a message is replayed into the NEXT turn's model
  // history. A system-authored line about the buyer's private negotiating stance
  // has no business in a prompt. Keeping it out here makes that structural
  // rather than a rule someone has to remember. (`msg.docNote` is a message
  // field, but it is computed BEFORE the message is appended and is never
  // patched afterwards -- a different shape, not a precedent for this one.)
  const [briefNotesByMessage, setBriefNotesByMessage] = useState(() => new Map());
  // ══ THE DOOR TO THE DECK, after Lane 709 / R3-3 ═══════════════════════════
  // Ryan: "I don't like this having it say prepare for a meeting in this little
  // pop up... it should be a button at the bottom of the chat. It comes up not
  // in this pop up modal." And: "you go through the chat and then you prepare
  // for the meeting... you don't always just wanna jump to prepare."
  //
  // So the upload card no longer offers a meeting AT ALL, and this is what
  // replaces it: the confirmed letter, held until the buyer has actually talked
  // to the chat about it, then rendered as one row at the BOTTOM OF THE CHAT.
  //
  // A MAP keyed by candidate id, not a single slot. Value shape:
  //   { candidateId, supplierId, supplierName, armedAtMessageCount }
  // `armedAtMessageCount` is `chatMessages.length` at the moment of the confirm,
  // and it is what makes this "born from the conversation" rather than born from
  // the upload: a door stays closed until an ASSISTANT message lands at or
  // after that mark. Without it this would be the card's button in a new
  // position, which is not what he asked for.
  //
  // ⚠️ A MAP BECAUSE THE RETIRED CARD RENDERED ONE DOOR PER SLOT. A single
  // slot here would let a second letter's confirm DESTROY the first letter's
  // door and reset the mark, leaving a window with zero doors until the next
  // reply — a narrower instance of the exact stranding class this lane exists
  // to close (consult-architect, 2026-08-02, defect 2). Keyed by candidate id,
  // an older released door keeps carrying the offer while a newer one waits.

  // D.5 Edit modal. Null when closed; { messageId, candidateId, payload } when
  // open. payload is the displayPayload ({entity_type, entity_name, predicate,
  // value}) that seeds the modal form.
  const [editingCandidate, setEditingCandidate] = useState(null);

  // D.7 provenance "+N more" expansion state, keyed by assistant message
  // index. Set rather than per-message id because assistant messages don't
  // carry an id today and indices stay stable under append-only chat history.
  const [expandedProvenanceByIdx, setExpandedProvenanceByIdx] = useState(() => new Set());

  // Secure-context guard: crypto.randomUUID requires HTTPS/localhost. The
  // fallback remains a UUID because the signed backend contract validates the
  // client turn id rather than accepting a display-only message id.
  const genMessageId = () =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
        const random = Math.random() * 16 | 0;
        const value = char === 'x' ? random : (random & 0x3 | 0x8);
        return value.toString(16);
      });

  // ── Document upload (Lane 616, Phase 5 "the front door") ─────────────────
  // Break #1: `POST /api/chat/upload/evaluate` has been live since Commit ε
  // with ZERO frontend callers. This is the door.
  //
  // SCOPE — screen ONE is the upload and its honest status. Screen TWO (Lane
  // 625) is the D7 confirm: "which supplier is this letter from?". Lane 618
  // shipped its endpoint, so the picker now mounts at the seam below.
  //
  // THE TWO BEATS. Upload classifies a supplier commercial notice, verifies
  // its claims and writes ZERO assertions, parking the row at
  // `awaiting_supplier`. The CONFIRM is the first and only moment anything is
  // attributed to a supplier. `attachState` therefore has its own vocabulary
  // (`utils/noticeAttachStatus.js`) and its own honesty rules — above all that
  // this endpoint answers 200 for outcomes that recorded nothing.
  //
  // CHAT CARVE-OUT: this block shares NO state with the chat pipeline. Upload
  // status is not a chat turn, so nothing is ever pushed into `chatMessages`
  // and no chat handler is touched.
  // ── A4 (Lane 660): SCANNING STARTS ON SUBMIT, NOT ON ATTACH ───────────────
  // Ryan, QA-1 finding 1: "when you upload the document, it immediately, like,
  // starts scanning it... I would have it start scanning it after the prompt is
  // submitted in case someone... attaches two documents to a prompt."
  //
  // So attaching now STAGES. Two pieces of state, deliberately separate because
  // they answer different questions:
  //
  //   `pending` — files the buyer has attached and Wingman has NOT sent. No
  //               request exists for these. They render as a tray, not a
  //               status card, because there is no status to report yet.
  //   `slots`   — one record per file Wingman has actually tried to read, each
  //               with its OWN upload/attach lifecycle.
  //
  // WHY `slots` IS A LIST AND NOT ONE OBJECT. The server takes exactly ONE file
  // per request — `routes/doc_upload.py` mounts `require_form_fields({"file"})`,
  // which 422s a second `file` part (Arc 5, CT-1). Two attachments are therefore
  // two requests, two `doc_upload_candidates` rows, two statuses and — for two
  // notices — two supplier confirms. One card summarising two documents would
  // be a claim the server never makes, so each file gets its own card.
  //
  // A per-slot `file` also fixes a latent bug in the single-state version: the
  // retry button read a module-level `lastFileRef`, so with more than one card
  // on screen "Read it again anyway" would have resent the most recent file
  // rather than that card's own.
  const [pending, setPending] = useState([]);
  const [slots, setSlots] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const attachSeq = useRef(0);
  const nextAttachId = () => { attachSeq.current += 1; return attachSeq.current; };
  // Guards a re-entrant confirm on one slot. Slot state is async, so the render
  // guard alone cannot stop a double click landing two attach requests.
  const attachInFlight = useRef(new Set());
  // Guards a setState after unmount when a slow read (a scanned PDF goes
  // through a vision call) outlives navigation away from Home.
  const uploadMountedRef = useRef(true);
  useEffect(() => {
    uploadMountedRef.current = true;
    return () => { uploadMountedRef.current = false; };
  }, []);

  // ── D50.5 (Lane 714): the automatic re-read fires ONCE PER FILE ───────────
  //
  // ⚠️ "Once per duplicate response" is NOT a bound, and believing it was is
  // the defect this ref exists to close. `no_assertions` is itself in the
  // server's `DEDUP_ELIGIBLE_STATUSES` (`helpers/doc_upload.py:170-187`), so
  // the auto-re-read's OWN candidate becomes the next dedup hit. Attach the
  // same empty file a third time and it would come back `duplicate` /
  // `prior_status='no_assertions'` and fire ANOTHER unrequested paid
  // extraction — a Claude vision OCR pass every time, forever, for a document
  // that has already proved twice it contains nothing.
  //
  // The `retry:true` recursion cannot bound this on its own: it only stops the
  // re-read re-entering ITSELF within one attach (the server bypasses dedup on
  // retry, so that response can never come back `duplicate`). It says nothing
  // about the NEXT attach, which starts from a clean slot.
  //
  // Keyed on the file's own identity rather than on a candidate id, because the
  // candidate id changes on every re-read — the dedup query returns the NEWEST
  // matching row — so it is the one thing that is not stable across exactly the
  // sequence being bounded. Session-scoped: a reload is a deliberate fresh
  // start, and a buyer who genuinely wants another read still has the button.
  const autoRereadDone = useRef(new Set());
  // Name + size, deliberately WITHOUT `lastModified`. Including the mtime would
  // make a re-downloaded copy of the same letter a different key, and it would
  // auto-re-read again — spending the extraction this bound exists to stop. The
  // trade is a false POSITIVE (two genuinely different files sharing a name and
  // an exact byte length), and that failure mode is benign: the card simply
  // does not read the second one automatically and offers the button instead.
  // Bounding spend matters more than catching every distinct file, and the
  // conservative direction is the one that cannot cost the customer money.
  const fileIdentity = (file) => `${file.name}:${file.size}`;
  const markAutoReread = (file) => autoRereadDone.current.add(fileIdentity(file));
  const shouldAutoReread = (state, wasRetry, file) => {
    if (!duplicateNonblockingEnabled) return false;
    // Structural belt-and-braces: a `retry:true` request bypasses dedup server
    // side and so cannot return `duplicate` at all. Asserting it here means the
    // recursion is bounded by this code rather than by that server behaviour.
    if (wasRetry) return false;
    if (duplicateAction(state) !== DUPLICATE_ACTION.REREAD) return false;
    return !autoRereadDone.current.has(fileIdentity(file));
  };

  const updateSlot = (id, patch) =>
    setSlots((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  // ── Lane 690: a ref mirror of `slots`, keyed by id ────────────────────────
  // The wait has to observe TWO things that land from different places: the
  // upload result (from `runUpload`) and the supplier confirm (from `runAttach`,
  // fired by the buyer clicking a card). Both write React state. A poll over a
  // ref mirror is the mechanism that can see both without the wait having to
  // subscribe to, or re-render on, either.
  //
  // ⚠️ THE MIRROR LAGS BY ONE RENDER, AND THE WAIT DEPENDS ON THAT BEING SAFE.
  // `setSlots` in `handleChatSubmit` and the first poll tick happen in the same
  // turn of the event loop, so for at least one tick the ids being waited on are
  // NOT in this map. `chatDocumentWait.slotWaitState` therefore treats an
  // unknown id as STILL READING rather than as settled — if it did the
  // opposite, the wait would release instantly and this lane would be a silent
  // no-op that passed every hand test. Pinned by
  // `test_unknown_id_is_still_waiting`.
  const slotsRef = useRef(new Map());
  // Lane 1400 WS1: slot ids not yet reported to a chat turn — files refused
  // before upload, and files from a Send that carried no question (which
  // forms no turn). The next Send with a question reports them. Used only
  // while `chatUploadHonestyEnabled` is on.
  const carriedSlotIdsRef = useRef([]);
  useEffect(() => {
    slotsRef.current = new Map(slots.map((s) => [s.id, s]));
  }, [slots]);

  /**
   * Hold the chat turn until every document attached to THIS submit has either
   * delivered something the answer can use or definitively will not.
   *
   * Scoped to `ids` on purpose: a card left unconfirmed on an earlier turn must
   * never be able to block a later one.
   *
   * Returns `{ skipped, timedOut, abandoned }` describing HOW the wait ended. It
   * never throws and never blocks forever — every exit path leads to the answer
   * being sent, EXCEPT `abandoned`, which is the one case where there is nobody
   * left to answer.
   */
  const awaitAttachedDocuments = async (ids) => {
    skipDocWaitRef.current = false;
    let phase = WAIT.READING;
    let phaseStartedAt = Date.now();

    // M4C WS5.4 (Lane 1119): send exactly the ids the UI truthfully calls
    // DELIVERED — the same `slotDelivered` predicate the wait/summary lines
    // use — not every slot that merely produced a candidate id. The backend
    // independently re-admits only readable statuses; this keeps the browser
    // statement and the backend input set identical.
    const successfulCandidateIds = () => ids
      .filter((id) => slotDelivered(slotsRef.current.get(id)))
      .map((id) => slotsRef.current.get(id)?.uploadState?.candidateId)
      .filter((id) => Number.isInteger(id) && id > 0);

    for (;;) {
      // ── UNMOUNT ABANDONS THE TURN ENTIRELY ────────────────────────────────
      // Matching `runUpload`/`runAttach`, but the consequence here is stronger
      // than theirs and is worth stating: they only skip a setState, whereas
      // this loop sits BEFORE the request is formed. Merely breaking out would
      // let the turn go on to POST an answer for a page that no longer exists —
      // a wasted paid call and a setState on a dead component.
      //
      // Found by a test, not by reading: the leaked request landed in the NEXT
      // test's fetch mock and failed an unrelated assertion. `fetch` is resolved
      // off the global at call time, so an abandoned turn genuinely does escape
      // its own component.
      if (!uploadMountedRef.current) {
        return { skipped: false, timedOut: false, abandoned: true, candidateIds: [] };
      }

      if (skipDocWaitRef.current) {
        return { skipped: true, timedOut: false, abandoned: false,
          candidateIds: successfulCandidateIds() };
      }

      const aggregate = aggregateWait(slotsRef.current, ids);
      if (aggregate.phase === WAIT.SETTLED) {
        return { skipped: false, timedOut: false, abandoned: false,
          candidateIds: successfulCandidateIds() };
      }

      // Each phase gets its own ceiling, measured from the moment that phase
      // began — so a slow read does not eat the buyer's time to answer the
      // confirm, and vice versa.
      if (aggregate.phase !== phase) {
        phase = aggregate.phase;
        phaseStartedAt = Date.now();
      }
      const ceiling = phase === WAIT.READING ? READ_DEADLINE_MS : CONFIRM_DEADLINE_MS;
      if (Date.now() - phaseStartedAt >= ceiling) {
        return { skipped: false, timedOut: true, abandoned: false,
          candidateIds: successfulCandidateIds() };
      }

      setChatWaitLine(describeWaiting(aggregate));
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }
  };

  /**
   * Attach without sending. Runs the client pre-flight only — a LOCAL type/size
   * check that forms no request, so this is not "scanning" in the sense Ryan
   * objected to. A file the client can already tell will be refused gets its
   * answer now instead of being staged to fail later; everything else waits for
   * Submit.
   */
  const stageFiles = (fileList) => {
    if (!docUploadEnabled) return;
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const refused = [];
    const accepted = [];
    files.forEach((file) => {
      const blocked = preflight(file, { csvEnabled: uploadTablesEnabled });
      if (blocked) {
        // No file on the slot: a refused type/size is not re-sendable, and the
        // card must not offer a retry that would earn the same refusal.
        refused.push({ id: nextAttachId(), file: null, uploadState: blocked, attachState: null, attachBusy: false });
      } else {
        accepted.push({ id: nextAttachId(), file });
      }
    });
    if (refused.length) setSlots((prev) => [...prev, ...refused]);
    if (refused.length && chatUploadHonestyEnabled) {
      carriedSlotIdsRef.current = [...carriedSlotIdsRef.current, ...refused.map((r) => r.id)];
    }
    if (accepted.length) setPending((prev) => [...prev, ...accepted]);
  };

  const unstageFile = (id) => setPending((prev) => prev.filter((p) => p.id !== id));

  const runUpload = async (slotId, file, { retry = false, announce = null } = {}) => {
    // First half of the double OFF-lock: with the flag off no request is even
    // formed. (The endpoint's own kill-switch check is the second half.)
    if (!docUploadEnabled || !file) return;

    // A re-read is about the same letter, so any previous confirm result on
    // this slot is stale. Clearing it stops "Recorded 3 claims from SKF"
    // sitting over a fresh read of the same file.
    //
    // `announce` lets the CALLER supply the in-flight line instead of the
    // generic one. D50.5's automatic re-read needs it: without it this write
    // would immediately clobber "Wingman already has this. Reading it again…"
    // with a bare "Reading…", and the buyer would be told a file is being read
    // with no hint that Wingman had it already or why it went back for it. That
    // is the whole of "it says what it is doing", so it is passed in rather
    // than written and overwritten. (Caught by rendering it, not by reading it.)
    updateSlot(slotId, {
      attachState: null,
      uploadState: announce || {
        outcome: OUTCOME.READING,
        headline: `Reading ${file.name}…`,
        detail: 'A scanned document takes longer. Wingman reads the page images.',
        canRetrySameFile: false,
      },
    });

    try {
      const { ok, httpStatus, body } = await uploadDocumentForEvaluation(
        authFetch, file, retry,
        chatUploadHonestyEnabled ? conversationRef.current : null);
      if (!uploadMountedRef.current) return;
      const next = ok
        ? describeOutcome(body, file.name)
        : describeHttpError(httpStatus, body && body.detail, file.name);

      // ══ SEAM — D50.5 (Lane 714): THE DUPLICATE STOPS BLOCKING ═════════════
      //
      // Ryan, round-3 QA: "I uploaded the file in this, and it said that it
      // already has a file in the system, probably from all my other QAs. And
      // then I had to hit… okay, so read it again." His decision: "You can say
      // read it again if it needs to, or maybe it doesn't need to read it
      // again. AS LONG AS IT PULLS THE INFORMATION."
      //
      // Detection stays. The MANDATORY CLICK goes: Wingman fires the re-read
      // itself and says so. The re-read parks the letter at `awaiting_supplier`
      // exactly as a first upload does, so the sender confirm — the ask D50.1
      // keeps — still runs, and with it `establish_notice_subject`, the ONLY
      // thing that makes the letter's supplier the conversation's subject. That
      // is what makes this the safe option rather than the cheap one; see
      // `duplicateAction`'s note for why reusing the prior parse would have
      // silently dropped the letter instead.
      if (shouldAutoReread(next, retry, file)) {
        // ⚠️ `next` IS NEVER WRITTEN ON THIS PATH. A duplicate whose prior row
        // is not parked reads as SETTLED to `chatDocumentWait.slotWaitState`,
        // and a slot that is SETTLED when the chat turn's poll looks releases
        // the turn — which would compose the answer WITHOUT the letter, the
        // exact defect Lane 690 exists to prevent, walking back in through the
        // door this lane opens.
        //
        // STATED HONESTLY, because the sabotage test says so: writing `next`
        // here and THEN recursing is not observably different today. Both
        // `setState`s land in one tick, React 18 batches them into a single
        // render, and the ref mirror never sees the duplicate state — the
        // sabotage that reintroduces that write does NOT fail any test in this
        // suite. So this ordering is not what makes the property true right
        // now; batching is.
        //
        // It is still the construction to keep, because it does not DEPEND on
        // batching. Insert one `await` between the two writes — or let the
        // recursive call early-return on its `!file` guard — and the batched
        // version leaks a settled duplicate while this version cannot. The
        // guarantee is structural here and incidental there.
        // `chat_turn_still_waits_for_the_re_read` covers the outcome that IS
        // observable (the turn does not answer before the confirm) and fails
        // when the auto-re-read is removed.
        markAutoReread(file);
        await runUpload(slotId, file, {
          retry: true,
          announce: describeRereading(file.name, body && body.prior_processed_at),
        });
        return;
      }

      updateSlot(slotId, { uploadState: next });
    } catch (e) {
      // authFetch turns a 401 into a forced logout + throw; a transport
      // failure lands here too. Either way we did not get an answer, and we
      // say so rather than guessing at one.
      if (!uploadMountedRef.current) return;
      updateSlot(slotId, { uploadState: describeNetworkError(file.name) });
    }
  };

  /**
   * Send every staged file, ONE AT A TIME.
   *
   * Sequential on purpose. The endpoint runs the whole pipeline — including a
   * Claude vision OCR pass on a scanned PDF — synchronously inside the request,
   * so firing N of them from one click would mean N concurrent paid model calls
   * against a 3-connection pool. The waiting files say they are waiting rather
   * than showing a spinner that implies work is happening on them.
   */
  const runQueue = async (queued) => {
    for (const item of queued) {
      if (!uploadMountedRef.current) return;
      // eslint-disable-next-line no-await-in-loop
      await runUpload(item.id, item.file);
    }
  };

  /**
   * Beat two: post the buyer's pick to the attach endpoint.
   *
   * `supplierId === null` is a first-class request (design §1's "supplier not
   * on file"), NOT a missing argument — it records the resolution and writes
   * zero assertions. `supplierName` is only a fallback for the card; the
   * server echoes the authoritative name from the row it resolved.
   */
  const runAttach = async (slotId, candidateId, supplierId, supplierName) => {
    // First half of the double OFF-lock, mirroring `runUpload`. Unreachable
    // through the UI today (the whole card is behind the same flag), so it is
    // defense-in-depth rather than something a test can exercise — stated
    // plainly rather than claimed as covered.
    if (!docUploadEnabled) return;
    // No id means no attachable row. Never form a request with `undefined` in
    // the path — it would 404 or, worse, hit some other candidate. The render
    // guard below keeps the picker off the screen in this state; this is the
    // second layer.
    if (candidateId === null || candidateId === undefined) return;
    if (attachInFlight.current.has(slotId)) return;

    attachInFlight.current.add(slotId);
    updateSlot(slotId, { attachBusy: true });
    try {
      // `conversationId` (A1, Lane 657) is what makes the confirmed supplier
      // the CHAT conversation's subject, so the buyer is not asked who they
      // mean a second time when they follow up about the letter.
      const { ok, httpStatus, body } =
        await attachSupplierToCandidate(authFetch, candidateId, supplierId, conversationId);
      if (!uploadMountedRef.current) return;
      const nextAttachState = ok
        ? describeAttachOutcome(body, supplierName)
        : describeAttachHttpError(httpStatus, body && body.detail);
      updateSlot(slotId, { attachState: nextAttachState });
    } catch (e) {
      // A transport failure, or authFetch's 401 logout. We did not get an
      // answer, so we do not claim to know whether anything was recorded.
      if (!uploadMountedRef.current) return;
      updateSlot(slotId, { attachState: describeAttachNetworkError() });
    } finally {
      attachInFlight.current.delete(slotId);
      if (uploadMountedRef.current) updateSlot(slotId, { attachBusy: false });
    }
  };

  const handleFilePicked = (e) => {
    const files = e.target.files;
    // Reset the input value so picking the SAME file twice still fires change.
    const picked = files ? Array.from(files) : [];
    e.target.value = '';
    stageFiles(picked);
  };

  const handleUploadDrop = (e) => {
    if (!docUploadEnabled) return;
    e.preventDefault();
    setDragActive(false);
    // Every dropped file is staged — dropping two documents onto the composer
    // is the same gesture as attaching two, and is the case Ryan named.
    stageFiles((e.dataTransfer && e.dataTransfer.files) || []);
  };

  // Only light up for an actual file drag — a text selection dragged across
  // the composer must not paint a drop target.
  const dragCarriesFile = (e) =>
    Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('Files');

  const handleUploadDragOver = (e) => {
    if (!docUploadEnabled || !dragCarriesFile(e)) return;
    e.preventDefault(); // required, or the browser refuses the drop
    if (!dragActive) setDragActive(true);
  };

  const handleUploadDragLeave = (e) => {
    // `currentTarget.contains(relatedTarget)` keeps the overlay stable while
    // the pointer crosses child elements inside the zone.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragActive(false);
  };

  // R2-A (Lane 311): the Daily Briefing card was removed (it was AI-fabricated
  // and superseded by the real data cards). Its state, fetch, and render are gone;
  // The orphaned /api/user/daily-briefing backend endpoint was deleted with the
  // daily briefing (Lane 1410 W7, 2026-10-17).

  useEffect(() => {
    inputRef.current?.focus();
  }, [token]);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // ─── Chat logic ───
  /**
   * @param {string} message
   * @param {object} [opts]
   * @param {function(): Promise<{docNote: string|null}>} [opts.waitFor]
   *   Lane 690. An async hook awaited AFTER the buyer's message is echoed into
   *   the transcript and the loading state is up, and BEFORE the request is
   *   formed. That ordering is the whole point: the composer must never look
   *   dead while the turn is held, so the echo happens first and the hold is
   *   visible. It resolves to an optional honest note to attach to the answer.
   *
   *   There is exactly ONE chat code path, with or without a wait. Adding a
   *   second send function would have been the easier change and would have
   *   left two places for the chat contract to drift.
   */
  /* ══ Lane 863 — THE PER-USER CONSENT GATE, NOW BEHIND A FLAG ══

     Both extractors below asked the same question: has THIS USER, in THIS
     BROWSER, clicked Allow on the one-time banner. `ALWAYS_ON_CAPTURE_ENABLED`
     retires that question — capture becomes a per-COMPANY decision only
     (build-plan D1/D3, LOCKED: *"it should always be on"*, *"that little pop up
     doesn't happen anymore for them"*).

     ⚠️ THE `||` SHORT-CIRCUIT IS LOAD-BEARING IN BOTH DIRECTIONS.
     Flag ON, `hasConsent` is never called: a stored "Not now" cannot suppress
     capture, and no localStorage read happens at all. Flag OFF, the right-hand
     side is the EXACT expression both call sites carried before this lane, so
     the flag-off path is not merely equivalent to today — it is the same call
     on the same value.

     ⚠️ THIS IS NOT A SUBSTITUTE FOR THE PER-COMPANY SWITCH, and must never
     become one. Each caller still ANDs its own kill switch
     (`chatCaptureEnabled` / `negotiationBriefEnabled`) IN FRONT of this. Those
     are the real gates and the admin controls (D4, Rule 103); this flag cannot
     open either of them, and a company with capture off stays off no matter
     what this says.

     ⚠️ A STORED "NO" IS OVERRIDDEN, DELIBERATELY. A user who clicked "Not now"
     before this flag existed has `chat_capture_consent_v1_<id>` = 'false' in
     localStorage; with the flag ON that value is ignored, not cleared. Ignoring
     is what makes the flag reversible — clearing the keys would re-prompt every
     user who had already answered if the flag were ever turned back off. The
     replacement for the vanished choice is DISCLOSURE plus retraction, which is
     why D-2's footer line and the Intelligence page are part of the same change
     and why D5 required the portal to be live first. */
  const perUserCaptureGateOpen = () =>
    alwaysOnCaptureEnabled || hasConsent(user?.user_id) === true;

  const sendChatMessage = async (message, opts = {}) => {
    if (!message.trim() || chatLoading || workbookChoice.locked) return;
    if (intakeChange) setIntakeChange(null);
    const requestConversationId = conversationId;
    const userMessage = message.trim();
    const userMessageId = genMessageId();
    const stillOwnsTurn = () => conversationRef.current === requestConversationId
      && chatMessagesRef.current.some(item => item.role === 'user' && item.id === userMessageId);
    const historyForBackend = buildChatHistory(chatMessages, userMessage);
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage, id: userMessageId }]);
    setChatLoading(true);
    let docNote = null;
    let workbookChange = null;
    try {
      if (typeof opts.waitFor === 'function') {
        // The wait itself never throws (every branch inside returns), but a
        // future edit could make it. A failed WAIT must not become a failed
        // TURN — the answer is what the buyer asked for, and it can still be
        // given. Fail toward sending, and say the document did not make it.
        try {
          const waited = await opts.waitFor();
          // The page is gone. Do not form the request: there is no answer to
          // render, and `fetch` resolves off the global at call time, so a turn
          // abandoned here really does outlive its own component.
          if (waited && waited.abort) return;
          if (!stillOwnsTurn()) return;
          docNote = (waited && waited.docNote) || null;
          if (waited && Array.isArray(waited.candidateIds)) {
            opts.documentCandidateIds = waited.candidateIds;
          }
          if (waited && Array.isArray(waited.failedUploads)) {
            opts.failedUploads = waited.failedUploads;
          }
        } catch (waitError) {
          console.error('[CHAT-DOC-WAIT-ERROR]', waitError);
          docNote = null;
        } finally {
          setChatWaitLine(null);
        }
      }
      if (conversationRef.current !== requestConversationId) return;
      const body = {
        query: userMessage,
        conversation_id: requestConversationId,
        history: historyForBackend,
        client_turn_id: userMessageId,
      };
      // Lane 1368 (S3): the RFP intake needs the buyer's time zone for dates.
      if (sourcingChatIntakeEnabled) body.time_zone = viewerZone();
      if (Array.isArray(opts.documentCandidateIds) && opts.documentCandidateIds.length > 0) {
        body.document_candidate_ids = opts.documentCandidateIds;
      }
      // Lane 1400 WS1: files that never got an id. Only ever set while
      // `chatUploadHonestyEnabled` is on (see handleChatSubmit).
      if (Array.isArray(opts.failedUploads) && opts.failedUploads.length > 0) {
        body.failed_uploads = opts.failedUploads;
      }
      // Lane 1398 Phase B: after "Change this workbook" this message IS the
      // change; the signed token names the workbook.
      workbookChange = workbookChoice.beginSubmit(userMessage);
      if (workbookChange) body.workbook_change = workbookChange;
      // ══ Lane 1082 (WB2) — the streamed turn, with the plain POST as the
      // mandatory fallback ══
      // `requestChatTurn` tries /api/ai-assistant/stream and degrades to the
      // exact fetch this block used to make on ANY transport problem (404 =
      // flag dark, disconnect, buffering proxy, old browser). The result shape
      // is identical for both paths, so everything below this call is
      // unchanged. Progress reports only the current operation; draft text
      // renders as soon as it arrives.
      const turnStartedAt = Date.now();
      const turn = await requestChatTurn({
        streamUrl: `${API_BASE}/api/ai-assistant/stream`,
        fallbackUrl: `${API_BASE}/api/ai-assistant`,
        headers: getAuthHeaders(),
        body,
        onTruncated: async () => {
          if (stillOwnsTurn()) {
            setChatStreamDraft(null);
            setChatProgress(prev => keepNotice(prev, { startedAt: turnStartedAt, line: 'Reconnecting to your answer…' }));
          }
          // Preserve the transport's same-turn replay fallback.
          return null;
        },
        onEvent: (event) => {
          if (!stillOwnsTurn()) return;
          // Keep the latest real operation; the progress component owns timing.
          // Answer deltas render immediately, independently of the status label.
          if (event.type === 'stage' && event.line) {
            setChatProgress(prev => keepNotice(prev, {
              startedAt: turnStartedAt,
              line: event.line,
              sourcesLine: prev?.sourcesLine || null,
            }));
          } else if (event.type === 'sources' && event.line) {
            setChatProgress(prev => keepNotice(prev, {
              startedAt: turnStartedAt,
              line: prev?.line || null,
              sourcesLine: event.line,
            }));
          } else if (event.type === 'heartbeat') {
            setChatProgress(prev => prev || ({
              startedAt: turnStartedAt, line: null, sourcesLine: null,
            }));
          } else if (event.type === 'attachment_status'
              && typeof event.notice?.text === 'string' && event.notice.text) {
            // Lane 1400 WS1: sent before any answer text. Server-written,
            // rendered as plain text above the draft.
            const notice = event.notice.text;
            setChatProgress(prev => ({
              startedAt: turnStartedAt, line: prev?.line || null,
              sourcesLine: prev?.sourcesLine || null, notice,
            }));
          } else if (event.type === 'answer_layout' && typeof event.version === 'string') {
            // Sent before any answer text: draw the draft with the layout.
            setChatStreamLayout(event.version);
          } else if (event.type === 'answer_delta' || event.type === 'answer_reset') {
            // Mirror Mode (M1): live answer tokens. Also ensure the progress
            // slot exists so the draft has somewhere to render.
            setChatStreamDraft(prev => mirrorStreamReduce(prev, event));
            setChatProgress(prev => prev || ({
              startedAt: turnStartedAt, line: null, sourcesLine: null,
            }));
          }
        },
      });
      // fetch() does NOT throw on a 5xx, and the backend's failure body
      // ({"detail": ...}) parses as JSON - so without this guard data.answer is
      // undefined and the bubble renders blank. Show a friendly message and
      // skip the capture dispatch below (Rule 108: dispatch fires only after a
      // successful reply). errData is best-effort; a non-JSON body (e.g. a 502
      // HTML page) yields null and the status code drives the message. A
      // streamed turn's error event carries the same status/detail contract.
      if (!stillOwnsTurn()) return;
      if (!turn.ok) {
        setChatMessages(prev => [...prev, { role: 'assistant', content: chatErrorMessage({ status: turn.status }, turn.errData), error: true, replyToMessageId: userMessageId }]);
        return;
      }
      const data = turn.data;
      if (workbookChange) {
        // A change that could not be planned goes back to editing with the
        // buyer's words in the composer.
        const restored = workbookChoice.finishSubmit(data);
        if (restored) setChatInput(restored);
      }
      const conversationDeckProposal = data.conversation_deck_proposal || null;
      const conversationDeckControl = data.conversation_deck_control || null;
      const conversationDeckProposalOutcome = data.conversation_deck_proposal_outcome || null;
      // Lane 1405: the deck builder's Open deck builder button (WINGMAN_DECK_BUILDER_ENABLED).
      const deckBuilder = data.deck_builder || null;
      // ══ Lane 1132 (Plan A) — the workbook offer + plan card ══
      // `data.report_offer` is ABSENT unless the server's creation gate is
      // ON for this tenant AND the model signaled report intent this turn;
      // `data.report_plan` is ABSENT unless a proposal was stored this
      // turn. Carried on the MESSAGE, so each
      // stays attached to the answer that earned it.
      // Lane 1398: one shared projection (offer, plan, started job, or the
      // request to retry starting) for every path that appends an answer.
      const reportFields = projectReportResult(data);
      // Lane 1322 D2: the signed "Create a draft event" offer, ABSENT unless
      // the sourcing gate is on for this company and the buyer asked for an
      // event; carried on the message like the other offers.
      const sourcingEventOffer = data.sourcing_event_offer || null;
      // Lane 1368 (S2): the RFP summary card and the "built" answer. ABSENT unless
      // SOURCING_CHAT_INTAKE_ENABLED and the sourcing gate are on for this company.
      const sourcingIntakeCard = data.sourcing_intake_card || null;
      const sourcingIntakeBuilt = data.sourcing_intake_built || null;
      // Lane 1400 WS1: the server's "I couldn't read <file>" notice. When it
      // is present it replaces the browser's own undelivered note for the
      // same files (one honest line, not two).
      const attachmentStatus = (chatUploadHonestyEnabled
        && typeof data.attachment_status?.text === 'string' && data.attachment_status.text)
        ? data.attachment_status.text : null;
      if (attachmentStatus) docNote = null;
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.answer, answerOrigin: data.answer_origin, ...(typeof data.answer_layout === 'string' ? { answerLayout: data.answer_layout } : {}), verification: data.verification, confidence: data.confidence_level, action: data.action, actionData: data.action_data, assertionsCited: data.assertions_cited, docNote, ...(attachmentStatus ? { attachmentStatus } : {}), conversationDeckProposal, conversationDeckControl, conversationDeckProposalOutcome, ...(deckBuilder ? { deckBuilder } : {}), ...reportFields, sourcingEventOffer, ...(sourcingIntakeCard ? { sourcingIntakeCard } : {}), ...(sourcingIntakeBuilt ? { sourcingIntakeBuilt } : {}), ...(Array.isArray(data.files) && (data.files.length || typeof data.files_note === 'string') ? { sandboxFiles: data.files } : {}), ...(typeof data.files_note === 'string' ? { sandboxFilesNote: data.files_note } : {}), groundingDiscarded: data.grounding_discarded === true, replyToMessageId: userMessageId }]);

      // Rule 108 hot-path discipline: evaluate fires ONLY after a successful
      // assistant reply, and is a fire-and-forget sync call (dispatch returns
      // void). If /api/ai-assistant throws, the catch block runs and we skip
      // dispatch entirely.
      // The server owns provenance and downstream eligibility. Keep the
      // inventory-status fallback only for older responses during rollout.
      const derivedFactResponse = data.capture_policy === 'exclude'
        && data.answer_origin === 'deterministic';
      if (chatCaptureEnabled && user?.user_id != null && perUserCaptureGateOpen()
          ) {
        // Chat-logging export: pass the AI reply text and per-session
        // conversation_id through to the candidate row. data.answer is the
        // formatted response just appended to chatMessages above; conversationId
        // comes from ChatContext (generated once per chat session as
        // `home-<ms>`). Both are nullable on the backend, so a missing answer
        // (defensive against an unexpected response shape) is safe.
        dispatcherRef.current?.dispatch(userMessageId, userMessage, null, (result) => {
          if (!result || result.error) return;
          const { status, candidate_id, display_payload, rationale } = result;
          if (status !== 'pending' && status !== 'auto_captured') return;
          const label = formatChipLabel(display_payload);
          setChipStatesByMessage(prev => {
            const next = new Map(prev);
            next.set(userMessageId, {
              kind: status === 'auto_captured' ? 'captured' : 'pending',
              candidateId: candidate_id,
              label,
              rationale: rationale || null,
              displayPayload: display_payload || null,
            });
            return next;
          });
          if (status === 'auto_captured') {
            showToast({
              // Lane 861 (Ryan's "one word everywhere") — was `Captured: ...`.
              // Names the destination, so the toast and the footer link and the
              // nav item all point at one thing with one name.
              message: `Added to Intelligence: ${label}`,
              actionLabel: 'Undo',
              onAction: () => handleUndoRetract(userMessageId, candidate_id),
              variant: 'info',
              durationMs: 5000,
            });
          }
        }, {
          assistantResponse: data.answer || null,
          conversationId,
          clientTurnId: userMessageId,
          derivedFactResponse,
          chatSurface: 'home',
        });
      } else {
        const reason = !chatCaptureEnabled ? 'kill-switch-off'
          : (user?.user_id == null) ? 'no-user-id'
          : 'consent-not-granted';
        console.log('[CHAT-CAPTURE] skipped', { reason });
      }

      // ══ Lane 763 / Phase 1b — THE NEGOTIATION-BRIEF EXTRACTOR ══
      //
      // A SECOND extractor over the SAME turn, asking a different question.
      // Chat capture asks "is this a fact about a supplier?"; this asks "does
      // this change how we negotiate with them?". Measured on 152 real XMEK
      // messages, the first question rejected every single one — including the
      // Singapore facility offer ("speculation and conditional reasoning") and
      // "I want to think about the key leverage points they have" ("strategic
      // reflection, not a factual statement"). Those rejections ARE the brief.
      //
      // THREE THINGS ABOUT THIS CALL, all deliberate:
      //
      //   1. `userMessage` is passed VERBATIM. Never trim it, never split it,
      //      never send only "the statement part". D61 (locked 2026-08-03):
      //      a qualifier, retraction or hedge must never be separated from the
      //      claim it qualifies — Lane 758 measured a stripped "it is
      //      unconfirmed" turning a disowned rumour into a 0.92 auto-capture.
      //      The backend has no discard path; that guarantee dies here if the
      //      client pre-chews the text.
      //
      //   2. `data.brief_scope` is ABSENT on most turns. The backend adds it
      //      only when the turn resolved to exactly one supplier, so the id is
      //      captured at the recognition boundary and never re-matched from a
      //      name downstream (§5.EV). No id → the endpoint skips before paying
      //      for a model call.
      //
      //   3. It is STILL fire-and-forget — the promise is deliberately not
      //      awaited, so it cannot enter /api/ai-assistant's latency budget
      //      (Rule 108). What Lane 773 (Phase 1c) added is a `.then` that
      //      RENDERS the result when it eventually lands, which is a different
      //      thing from waiting for it: the answer is on screen either way, and
      //      a slow or failed extraction changes nothing the buyer sees.
      //
      // The consent gate is chat capture's, on purpose: a second model pass over
      // the same message is the same class of processing as the first, so the
      // two must answer to the same per-user question rather than drifting
      // apart. Lane 863 keeps that coupling and moves BOTH behind
      // `ALWAYS_ON_CAPTURE_ENABLED` together — see `perUserCaptureGateOpen`.
      // Leaving this one on `hasConsent` while lifting the other would have
      // shipped "always on, except the extractor nobody remembers", and the
      // banner is the ONLY writer of that key, so with the banner unrendered
      // this gate would have been permanently shut instead of merely stricter.
      //
      // ⚠️ ITS OWN PER-COMPANY SWITCH IS STILL IN FRONT AND IS STILL NOT
      // chatCaptureEnabled. `negotiationBriefEnabled` is TRUE for XMEK (91) on
      // PROD (verified read-only 2026-08-05), so this is a live path for a real
      // customer the moment the flag flips — not a dormant one.
      if (negotiationBriefEnabled && user?.user_id != null
          && perUserCaptureGateOpen()) {
        // ══ Lane 773 / Phase 1c — THE SILENT WRITE STOPS BEING SILENT ══
        //
        // Measured by Lane 770 on staging: a brief entry binds to whichever
        // supplier the conversation is anchored on and the buyer is told
        // NOTHING. Ryan asked mid-QA whether it knew he meant AFT or Precision
        // Urethane; it bound to AFT correctly — but from a letter uploaded 9
        // turns and 15 minutes earlier, and nothing in his sentence resolved
        // it. A wrong bind silences that argument on a deck he was not
        // thinking about, leaves it live on the one he was, persists 12
        // months, and is discoverable only by opening the OTHER supplier's
        // deck builder.
        //
        // ⚠️ THE SUPPLIER NAME IS THE SERVER'S, READ OFF THE ROWS THAT WERE
        // WRITTEN. It is never composed here from `data.brief_scope` — that
        // would be re-deriving the subject from the request instead of
        // reporting the subject of the write, and if those two could ever
        // differ the whole disclosure is worthless.
        //
        // ⚠️ AND IT IS AN UNDO, NOT JUST A NOTICE. Disclosure alone would tell
        // the buyer a mis-bind happened and still leave the remedy as "go open
        // that supplier's deck" — the exact discovery path the recon filed as
        // the problem. `retractBriefEntry` is soft-delete (Rule 107): the row
        // stays for the audit trail, and the retract route is gated by the
        // same per-company flag that allowed the write, so Undo cannot
        // dead-end.
        evaluateBrief({
          rawText: userMessage,
          supplierId: data.brief_scope?.supplier_id ?? null,
          assistantResponse: data.answer || null,
          conversationId,
          clientTurnId: userMessageId,
          derivedFactResponse,
        }).then((result) => {
          const disclosure = result?.disclosure;
          if (result?.status !== 'captured' || !disclosure?.text) return;
          setBriefNotesByMessage(prev => {
            const next = new Map(prev);
            next.set(userMessageId, {
              text: disclosure.text,
              supplierId: disclosure.supplier_id,
              assertionIds: disclosure.assertion_ids || [],
            });
            return next;
          });
          showToast({
            message: disclosure.text,
            actionLabel: 'Undo',
            onAction: () => handleUndoBriefWrite(userMessageId, disclosure),
            variant: 'info',
            durationMs: 8000,
          });
        }).catch((err) => {
          // `evaluateBrief` documents that it never rejects. This exists so a
          // future edit to that contract cannot turn a brief extraction into
          // an unhandled rejection on the live chat path.
          console.log('[BRIEF-EXTRACT] disclosure handler failed',
            err?.message || err);
        });
      } else {
        const reason = !negotiationBriefEnabled ? 'kill-switch-off'
          : (user?.user_id == null) ? 'no-user-id'
          : 'consent-not-granted';
        console.log('[BRIEF-EXTRACT] skipped', { reason });
      }
    } catch (error) {
      if (!stillOwnsTurn()) return;
      setChatMessages(prev => [...prev, { role: 'assistant', content: GENERIC_MESSAGE, error: true, replyToMessageId: userMessageId }]);
    } finally {
      // Lane 1398 Phase B: a change that did not finish (error, cut stream,
      // conversation switched) goes back to editing with its text, never
      // leaving the composer locked.
      if (workbookChange) {
        const draft = workbookChoice.failSubmit();
        if (draft && conversationRef.current === requestConversationId) {
          setChatInput(draft);
        }
      }
      setChatLoading(false);
      // Belt-and-braces with the `finally` around the wait itself: the waiting
      // line must never outlive the turn, or it would sit under a delivered
      // answer still claiming Wingman is reading.
      setChatWaitLine(null);
      // Same rule for the progress line (Lane 1082): it narrates a running
      // turn and must die with it, success or failure.
      setChatProgress(null);
      // And for the Mirror answer draft (Lane 1103): the final bubble is the
      // authority; a partial stream must never survive the turn.
      setChatStreamDraft(null);
      setChatStreamLayout(null);
    }
  };

  // ─── Lane 1364: the deck card asked which supplier ───
  // The pick becomes one ordinary chat message naming the supplier in full;
  // the server resolves it against the company's own suppliers and the
  // normal deck card follows. The question collapses to "Selected: <name>"
  // so it cannot be answered twice.
  const pickDeckSupplier = (msgIdx, supplierName) => {
    // Lane 1398 Phase B: every send path respects the workbook lock.
    if (chatLoading || workbookChoice.locked || !supplierName) return;
    setChatMessages(prev => prev.map((m, i) => (
      i === msgIdx ? { ...m, deckSupplierPicked: supplierName } : m
    )));
    sendChatMessage(deckSupplierReply(supplierName));
  };

  // ─── Chat capture chip handlers ───

  const handleConfirmChip = async (messageId, candidateId) => {
    setChipStatesByMessage(prev => {
      const next = new Map(prev);
      const cur = next.get(messageId);
      if (cur) next.set(messageId, { ...cur, disabled: true });
      return next;
    });
    try {
      await confirmCandidate(candidateId);
      setChipStatesByMessage(prev => {
        const next = new Map(prev);
        const cur = next.get(messageId);
        if (!cur) return prev;
        next.set(messageId, { kind: 'captured', candidateId, label: cur.label, rationale: cur.rationale });
        return next;
      });
    } catch (err) {
      if (err?.status === 409) {
        // Terminal: candidate was already resolved in another tab/request.
        setChipStatesByMessage(prev => {
          const next = new Map(prev);
          const cur = next.get(messageId);
          if (!cur) return prev;
          next.set(messageId, { ...cur, kind: 'already_saved', disabled: true });
          return next;
        });
      } else {
        // Transient (5xx / network): re-enable buttons, surface a retry toast.
        setChipStatesByMessage(prev => {
          const next = new Map(prev);
          const cur = next.get(messageId);
          if (!cur) return prev;
          next.set(messageId, { ...cur, disabled: false });
          return next;
        });
        showToast({ message: "Couldn't save. Try again.", variant: 'error', durationMs: 4000 });
      }
    }
  };

  const handleDismissChip = async (messageId, candidateId) => {
    // Optimistic — user's intent is to not save. Nothing to restore on failure.
    setChipStatesByMessage(prev => {
      const next = new Map(prev);
      next.delete(messageId);
      return next;
    });
    try {
      await dismissCandidate(candidateId);
    } catch (_) {
      // Silent: nothing was saved, user wanted nothing saved. Acceptable.
    }
  };

  // D.5 — open the Edit modal. Synchronous by design (Rule 108): this
  // handler returns immediately; the modal owns its own save Promise.
  const handleEditChip = (messageId, candidateId, displayPayload) => {
    setEditingCandidate({ messageId, candidateId, payload: displayPayload });
  };

  const handleEditModalClose = () => {
    setEditingCandidate(null);
  };

  // 200 success from the modal — flip the chip to captured with the edited label.
  const handleEditSaveSuccess = (newPayload) => {
    const target = editingCandidate;
    if (!target) return;
    const newLabel = formatChipLabel(newPayload);
    setChipStatesByMessage(prev => {
      const next = new Map(prev);
      const cur = next.get(target.messageId);
      if (!cur) return prev;
      next.set(target.messageId, {
        ...cur,
        kind: 'captured',
        label: newLabel,
        displayPayload: newPayload,
        disabled: false,
      });
      return next;
    });
    setEditingCandidate(null);
  };

  // 409 from the modal — candidate was resolved elsewhere. Flip chip to
  // already_saved and close the modal. No toast, mirroring handleConfirmChip.
  const handleEdit409 = () => {
    const target = editingCandidate;
    if (!target) return;
    setChipStatesByMessage(prev => {
      const next = new Map(prev);
      const cur = next.get(target.messageId);
      if (!cur) return prev;
      next.set(target.messageId, { ...cur, kind: 'already_saved', disabled: true });
      return next;
    });
    setEditingCandidate(null);
  };

  const handleUndoRetract = async (messageId, candidateId) => {
    try {
      await retractCandidate(candidateId);
      setChipStatesByMessage(prev => {
        const next = new Map(prev);
        next.delete(messageId);
        return next;
      });
      // Lane 861 — was 'Capture undone'. Symmetric with the add toast above.
      showToast({ message: 'Removed from Intelligence', variant: 'info', durationMs: 3000 });
    } catch (_) {
      showToast({ message: 'Could not undo. Already handled.', variant: 'error', durationMs: 4000 });
    }
  };

  /**
   * Lane 773 / Phase 1c — undo the brief entries THIS turn wrote.
   *
   * ⚠️ BY `assertion_id`, ONE ROW AT A TIME — never by `lever_key`. The store
   * refuses a `lever_key` retract for unmapped kills precisely because that
   * path clears EVERY live row for that lever, which here would also undo the
   * times the buyer ruled the same argument out in earlier turns. He is
   * undoing what he was just told about, and nothing else.
   *
   * Sequential, not `Promise.all`: the backend pool is three connections on one
   * worker, and a turn writes at most `MAX_ITEMS_PER_TURN` (4) entries.
   *
   * The note is cleared only on a clean sweep. A partial failure leaves it on
   * screen, because the thing it describes is partly still stored — telling the
   * buyer it is gone when one row survived would be the same silent-
   * disagreement failure this whole programme exists to fix.
   */
  const handleUndoBriefWrite = async (messageId, disclosure) => {
    const ids = disclosure?.assertion_ids || [];
    const supplierId = disclosure?.supplier_id;
    if (!supplierId || ids.length === 0) return;
    let failed = 0;
    for (const assertionId of ids) {
      try {
        await retractBriefEntry({ supplierId, assertionId });
      } catch (err) {
        failed += 1;
        console.log('[BRIEF-EXTRACT] retract failed',
          { assertionId, message: err?.message || String(err) });
      }
    }
    if (failed > 0) {
      showToast({ message: 'Could not undo that. Open the supplier’s brief to check.',
        variant: 'error', durationMs: 5000 });
      return;
    }
    setBriefNotesByMessage(prev => {
      const next = new Map(prev);
      next.delete(messageId);
      return next;
    });
    showToast({ message: 'Removed from the negotiation brief.', variant: 'info', durationMs: 3000 });
  };

  // D.7 provenance chip cap: at most this many chips render below any single
  // assistant bubble; the rest collapse behind a "+N more" affordance that
  // expands inline. Hardcoded — see docs/commit-d7-scope.md Decision 3.
  const PROVENANCE_CHIP_CAP = 8;

  // D.7 + Item 1.6a — assistant-side provenance render. Mirrors
  // renderCaptureChip in intent (chip beneath a chat bubble) but renders
  // below the AI's answer rather than below the user's message. Filter,
  // rationale, and deep-link routing all live in utils/provenanceChip.js
  // so the allowlist and per-source phrasing can be edited in one place.
  // Provenance chips hidden 2026-06-02 pending Step-1 prompt redesign
  // (see docs/2026-06-02_reorientation.md). Flip PROVENANCE_CHIPS_ENABLED
  // back to true to restore; plumbing, filter, sort, and assertionsCited
  // prop are all intact.
  const PROVENANCE_CHIPS_ENABLED = false;

  const renderAssistantProvenance = (msg, msgIdx) => {
    if (!PROVENANCE_CHIPS_ENABLED) return null;
    if (!Array.isArray(msg.assertionsCited) || msg.assertionsCited.length === 0) return null;
    const chips = msg.assertionsCited
      .filter(shouldRenderChip)
      .slice()
      .sort((a, b) => {
        // Confidence-desc; Array.prototype.sort is stable in V8/JSCore so
        // equal confidences preserve insertion order from the backend.
        const ca = typeof a.confidence === 'number' ? a.confidence : 0;
        const cb = typeof b.confidence === 'number' ? b.confidence : 0;
        return cb - ca;
      });
    if (chips.length === 0) return null;

    const isExpanded = expandedProvenanceByIdx.has(msgIdx);
    const visible = isExpanded ? chips : chips.slice(0, PROVENANCE_CHIP_CAP);
    const hidden = chips.length - visible.length;

    const moreBtnStyle = {
      padding: '3px 10px',
      border: '1px solid var(--color-border)',
      borderRadius: '100px',
      backgroundColor: '#FFFFFF',
      color: 'var(--color-text-secondary)',
      cursor: 'pointer',
      fontSize: '11px',
      fontWeight: 500,
    };

    return (
      <div
        data-testid="provenance-chips"
        style={{ display: 'flex', flexWrap: 'wrap', gap: '6px',
                 marginTop: '12px', paddingTop: '8px',
                 borderTop: '1px solid var(--color-border-light)' }}
      >
        {visible.map((a, i) => {
          const href = getProvenanceDeepLink(a);
          return (
            <CaptureChip
              key={`${a.assertion_id ?? 'a'}-${a.candidate_id ?? a.entity_id ?? 'e'}-${i}`}
              variant="provenance"
              label={getProvenanceLabel(a)}
              rationale={getProvenanceRationale(a)}
              candidateId={a.candidate_id}
              onClick={href ? () => navigate(href) : undefined}
            />
          );
        })}
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpandedProvenanceByIdx(prev => {
              const next = new Set(prev);
              next.add(msgIdx);
              return next;
            })}
            style={moreBtnStyle}
          >
            +{hidden} more
          </button>
        )}
      </div>
    );
  };

  const renderCaptureChip = (msgId) => {
    const chip = chipStatesByMessage.get(msgId);
    if (!chip) return null;
    const wrapper = { display: 'flex', justifyContent: 'flex-end', marginTop: '6px' };
    if (chip.kind === 'pending') {
      return (
        <div style={wrapper}>
          <CaptureChip
            variant="confirm"
            label={chip.label}
            rationale={chip.rationale}
            candidateId={chip.candidateId}
            disabled={Boolean(chip.disabled)}
            onConfirm={() => handleConfirmChip(msgId, chip.candidateId)}
            onEdit={() => handleEditChip(msgId, chip.candidateId, chip.displayPayload)}
            onDismiss={() => handleDismissChip(msgId, chip.candidateId)}
          />
        </div>
      );
    }
    if (chip.kind === 'already_saved') {
      return (
        <div style={wrapper}>
          <CaptureChip
            variant="confirm"
            label={chip.label}
            rationale={chip.rationale}
            candidateId={chip.candidateId}
            disabled={true}
          />
        </div>
      );
    }
    if (chip.kind === 'captured') {
      return (
        <div style={wrapper}>
          <CaptureChip
            variant="captured"
            label={chip.label}
            rationale={chip.rationale}
            candidateId={chip.candidateId}
          />
        </div>
      );
    }
    return null;
  };

  /**
   * Lane 773 / Phase 1c — the disclosure, beneath the message that caused it.
   *
   * Sits with `renderCaptureChip` under the USER bubble rather than under the
   * answer, because that is what it is about: the buyer's own sentence, and
   * what the system recorded off the back of it. Two extractors can both fire
   * on one message, so a capture chip and this note can legitimately appear
   * together — they are two different records, and saying so is the point.
   *
   * Quiet by design: it is a footnote on a turn, not a second answer.
   */
  const renderBriefNote = (msgId) => {
    const note = briefNotesByMessage.get(msgId);
    if (!note) return null;
    return (
      <div className="hm-briefnote">
        <span>{note.text}</span>
        <button
          type="button"
          className="hm-briefnote-undo"
          onClick={() => handleUndoBriefWrite(msgId, {
            supplier_id: note.supplierId,
            assertion_ids: note.assertionIds,
          })}
        >
          Undo
        </button>
      </div>
    );
  };

  // ── M4B WS4b (Lane 1117): composer growth + keyboard contract ──────────
  // When the input clears (successful send, explicit clear), the box returns
  // to one line -- growth must never outlive the text that caused it.
  useEffect(() => {
    if (!chatInput && inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.overflowY = 'hidden';
    }
  }, [chatInput]);

  // Height is content-driven: reset to auto, then cap scrollHeight at four
  // 20px lines (80px). Past the cap the textarea scrolls internally.
  const autogrowComposer = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    const capped = Math.min(el.scrollHeight, 80);
    el.style.height = `${capped}px`;
    el.style.overflowY = el.scrollHeight > 80 ? 'auto' : 'hidden';
  };

  // Enter submits exactly once; Shift+Enter is a newline; an Enter that ends
  // an IME composition commits the text and must NEVER send.
  const handleComposerKeyDown = (e) => {
    // Enter WITHOUT modifiers submits (Codex M4B review finding 3): Shift
    // makes a newline; Ctrl/Alt/Meta+Enter are deliberate non-sends too --
    // the promise is 'Enter without modifiers', not 'Enter unless Shift'.
    if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
    if (e.nativeEvent && e.nativeEvent.isComposing) return;
    e.preventDefault();
    handleChatSubmit(e);
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();

    // ── A4 (Lane 660): submit is where reading starts ──────────────────────
    // Everything staged goes now, each as its own request (see `runQueue`).
    //
    // ── Lane 690: AND submit is where the turn WAITS for it ────────────────
    // The comment that stood here described a deliberate carve-out — "the
    // uploads are fired alongside the chat turn, NOT awaited before it" — and
    // named this as a different lane's decision. This IS that lane, so the
    // comment is rewritten rather than left to describe behaviour the code no
    // longer has.
    //
    // Ryan attached a letter, asked a question in the same Send, and got an
    // answer composed while the document was still being read. Behind
    // `chatAwaitsDocumentEnabled`, the turn now holds until every document
    // attached to THIS submit has either delivered something the answer can use
    // or definitively will not — including the buyer's "who sent this?" confirm,
    // which is the only moment a notice's claims become readable at all.
    //
    // Flag OFF, or nothing attached: byte-identical to before. `waitFor` is
    // simply not supplied, and `sendChatMessage` is called exactly as it was.
    let docIds = [];
    if (pending.length > 0) {
      const queued = pending;
      setPending([]);
      setSlots((prev) => [
        ...prev,
        ...queued.map((q, i) => ({
          id: q.id,
          file: q.file,
          attachState: null,
          attachBusy: false,
          // The first file starts immediately; the rest say they are waiting.
          // `runUpload` overwrites each with a real "Reading…" as its turn
          // comes, so a queued card never shows a spinner for work that has
          // not begun.
          uploadState: i === 0
            ? {
              outcome: OUTCOME.READING,
              headline: `Reading ${q.file.name}…`,
              detail: 'A scanned document takes longer. Wingman reads the page images.',
              canRetrySameFile: false,
            }
            : {
              outcome: OUTCOME.READING,
              headline: `${q.file.name} is queued.`,
              detail: 'Wingman reads one document at a time. This one starts when the one above finishes.',
              canRetrySameFile: false,
            },
        })),
      ]);
      docIds = queued.map((q) => q.id);
      runQueue(queued);
    }

    // The wait is entered ONLY when this submit carried files AND the dial is
    // on. An ordinary question is therefore not one tick slower — it takes the
    // identical code path it took before this lane, with `opts` empty.
    // Lane 1400 WS1: with honest uploads on, this turn also reports files
    // carried from earlier Sends that formed no turn, and it waits for its
    // files even when the CHAT_AWAITS_DOCUMENT dial is off (an outcome must
    // be known to be reported). A Send with no question forms no turn, so
    // its files are carried to the next one instead.
    let turnIds = docIds;
    if (chatUploadHonestyEnabled) {
      if (chatInput.trim()) {
        turnIds = [...carriedSlotIdsRef.current, ...docIds];
        carriedSlotIdsRef.current = [];
      } else {
        carriedSlotIdsRef.current = [...carriedSlotIdsRef.current, ...docIds];
        turnIds = [];
      }
    }
    const waitFor =
      (chatAwaitsDocumentEnabled || chatUploadHonestyEnabled) && turnIds.length > 0
        ? async () => {
          const { skipped, timedOut, abandoned, candidateIds } = await awaitAttachedDocuments(turnIds);
          if (abandoned) return { abort: true, docNote: null };
          if (chatUploadHonestyEnabled) {
            return {
              abort: false,
              docNote: describeUndelivered(slotsRef.current, turnIds, { skipped, timedOut }),
              candidateIds: turnCandidateIds(slotsRef.current, turnIds),
              failedUploads: turnFailedUploads(slotsRef.current, turnIds),
            };
          }
          return {
            abort: false,
            docNote: describeUndelivered(slotsRef.current, docIds, { skipped, timedOut }),
            candidateIds,
          };
        }
        : undefined;

    // No-ops on an empty message, which is exactly right when the buyer
    // attached a document and pressed Send without typing anything. Note the
    // consequence, which is correct and deliberate: attaching a file with NO
    // question runs the uploads and waits for nothing, because there is no turn
    // to hold.
    sendChatMessage(chatInput, {
      waitFor,
      // Candidate IDs are populated by the same-Send wait. They are copied
      // into the request only after the wait resolves, so no prior slot can
      // leak into this turn.
      documentCandidateIds: [],
    });
  };

  // Lane 1369: the chat no longer offers the old RFP builder. The "Create
  // Draft RFP" button (a `create_rfp` suggested action that called
  // /api/rfp/create-from-context) and the "Open RFP" link it produced are
  // gone; that was the only suggested action this page rendered. Sourcing
  // events replace the old builder (Ryan, 2026-10-03). The backend endpoint is
  // kept for now.

  // ─── Formatting ───
  const formatMessage = (content) => {
    if (!content) return '';
    const processInline = (text, keyPrefix) => {
      if (!text) return text;
      const parts = text.split(/\*\*(.+?)\*\*/g);
      if (parts.length === 1) return text;
      return parts.map((part, i) => i % 2 === 1 ? <strong key={`${keyPrefix}-b${i}`} style={{ fontWeight: '600', color: 'var(--color-text-primary)' }}>{part}</strong> : part);
    };
    return content.split('\n').map((line, idx) => {
      const trimmed = line.trim();
      if (trimmed.startsWith('<') && (trimmed.startsWith('<metadata') || trimmed.startsWith('</') || trimmed.startsWith('<confidence') || trimmed.startsWith('<actions') || trimmed.startsWith('<action ') || trimmed.startsWith('<data_gaps') || trimmed.startsWith('<sources_used'))) return null;
      if (trimmed.startsWith('### ')) return <div key={idx} style={{ fontWeight: '600', fontSize: 'var(--font-size-xs)', marginTop: '12px', marginBottom: '3px', color: 'var(--color-text-primary)' }}>{processInline(trimmed.slice(4), idx)}</div>;
      if (trimmed.startsWith('## ')) return <div key={idx} style={{ fontWeight: '700', fontSize: 'var(--font-size-sm)', marginTop: '14px', marginBottom: '4px', color: 'var(--color-text-primary)' }}>{processInline(trimmed.slice(3), idx)}</div>;
      if (trimmed.startsWith('# ')) return <div key={idx} style={{ fontWeight: '700', fontSize: 'var(--font-size-base)', marginTop: '16px', marginBottom: '5px', color: 'var(--color-text-primary)' }}>{processInline(trimmed.slice(2), idx)}</div>;
      if (/^\d+\.\s/.test(trimmed)) return <div key={idx} style={{ margin: '4px 0', paddingLeft: '12px' }}>{processInline(trimmed, idx)}</div>;
      if (trimmed.startsWith('•') || (trimmed.startsWith('- ') && trimmed.length > 2) || trimmed.startsWith('* ')) {
        const bulletContent = trimmed.startsWith('•') ? trimmed.slice(1).trim() : trimmed.slice(2).trim();
        return <div key={idx} style={{ margin: '4px 0', paddingLeft: '16px', display: 'flex', gap: '8px' }}><span style={{ color: 'var(--color-text-muted)', flexShrink: 0 }}>•</span><span>{processInline(bulletContent, idx)}</span></div>;
      }
      if (trimmed === '---' || trimmed === '***' || trimmed === '___') return <div key={idx} style={{ borderTop: '1px solid var(--color-border)', margin: '12px 0' }} />;
      if (trimmed === '') return <div key={idx} style={{ height: '8px' }} />;
      return <div key={idx} style={{ margin: '4px 0', lineHeight: '1.6' }}>{processInline(line, idx)}</div>;
    }).filter(Boolean);
  };

  // Empty-state intent buttons (W1-5). Presentation only — each intent either
  // focuses the composer or routes; none touch the chat message pipeline.
  //
  // Lane 251 (PROD dark-room mitigation): the two destination-bearing intents
  // OFF-lock on their /me tenant flags so a flag-dark tenant is never shown a
  // door to a dark destination — on PROD (flags OFF) "/opportunities" renders a
  // blank page and "/decks/meeting-prep" reaches a dormant deterministic-only
  // deck. "Start a chat" has no destination (it focuses the composer) and
  // always renders. Staging (all flags ON) keeps all three.
  //   - opportunities: this button gate is the frontend half of the established
  //     double-OFF-lock (App.js also route-gates /opportunities on
  //     opportunitiesEnabled — Lane 42).
  //   - deck: button-level gate ONLY — App.js's /decks/meeting-prep route is
  //     intentionally left registered (the filed mitigation targets the Home
  //     door, and the route renders a functional deterministic deck, not a
  //     blank page). This is a single lock, not a double lock.
  const intents = [
    { label: 'Start a chat', onClick: () => inputRef.current?.focus() },
    ...(templateDecksEnabled
      ? [{ label: 'Prepare for a meeting', onClick: () => navigate('/decks/meeting-prep') }]
      : []),
    ...(opportunitiesEnabled
      ? [{ label: 'Review savings opportunities', onClick: () => navigate('/opportunities') }]
      : []),
  ];

  const hasConversation = chatMessages.length > 0;

  // Lane 1368: a live RFP draft moved into this chat (the banner's Continue, and the
  // version 2 draft bar's Open): the server's answer and its fresh card.
  // `forConversation` (version 2's bar): the chat it was opened in; an answer that
  // comes back after the buyer started another chat is not added to that one.
  const onIntakeResumed = (out, forConversation) => {
    if (forConversation !== undefined && forConversation !== conversationRef.current) return;
    setChatMessages((prev) => [...prev, {
      role: 'assistant',
      content: out.answer,
      ...(out.sourcing_intake_card ? { sourcingIntakeCard: out.sourcing_intake_card } : {}),
    }]);
  };
  // Lane 1439 (version 2 only): the newest card of each RFP draft, and the one card
  // that can still be changed in chat (the change box only ever opens for it).
  const newestIntakeCard = intakeV2 ? newestIntakeCards(chatMessages) : null;
  const liveIntakeIdx = intakeV2 ? liveIntakeCardIndex(chatMessages) : -1;
  const liveIntakeId = liveIntakeIdx >= 0 ? chatMessages[liveIntakeIdx].sourcingIntakeCard.intake_id : null;
  const changeOpen = !!(intakeChange && liveIntakeId && intakeChange.intakeId === liveIntakeId);
  const focusComposerEnd = () => {
    window.requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
      autogrowComposer(el);
    });
  };
  const toggleIntakeChange = (card) => {
    if (changeOpen) {
      setIntakeChange(null);
      return;
    }
    setIntakeChange({ intakeId: card.intake_id, topic: null });
    focusComposerEnd();
  };
  // A suggestion that needs a name or a date: its start goes in the chat box, cursor
  // at the end. The buyer finishes it and sends it like any message.
  const fillIntakeChange = (text) => {
    setChatInput(text);
    focusComposerEnd();
  };

  // ── Upload status card (Lane 616) ────────────────────────────────────────
  // Tone and icon are keyed off the OUTCOME class, never off the raw status
  // string, so a status this build does not recognise cannot inherit the
  // "read" styling. `describeOutcome` fails closed to OUTCOME.UNKNOWN, which
  // lands in the failure branch here — never the success one.
  //
  // Lane 625 extends the SAME map with the confirm's vocabulary. The two
  // outcome enums overlap only on `failed` and `unknown`, and both mean `bad`
  // in either, so one map is unambiguous. `ask` is new: read, verified, and
  // attributed to nobody — a question, not a verdict.
  const UPLOAD_TONE = {
    [OUTCOME.READING]: 'busy',
    [OUTCOME.READ]: 'ok',
    [OUTCOME.NOTHING_FOUND]: 'warn',
    [OUTCOME.AWAITING_SUPPLIER]: 'ask',
    [OUTCOME.DUPLICATE]: 'warn',
    [OUTCOME.BLOCKED]: 'warn',
    [OUTCOME.FAILED]: 'bad',
    [OUTCOME.UNKNOWN]: 'bad',
    [ATTACH_OUTCOME.ATTACHING]: 'busy',
    [ATTACH_OUTCOME.ATTACHED]: 'ok',
    // A partial and a zero-recorded attach are NOT success. `nothing_recorded`
    // is `bad` on purpose: it is the 200 that stored nothing.
    [ATTACH_OUTCOME.PARTIAL]: 'warn',
    [ATTACH_OUTCOME.NOTHING_RECORDED]: 'bad',
    [ATTACH_OUTCOME.ATTACHED_EMPTY]: 'warn',
    // NEUTRAL, not `warn` (D31(c)). "This letter is not from a supplier on
    // file" is the outcome the buyer deliberately chose, and it was recorded
    // exactly as asked. Nothing went wrong and nothing needs checking, so it
    // gets no amber — it is information.
    [ATTACH_OUTCOME.NOT_ON_FILE]: 'neutral',
  };

  // Keyed off the resolved TONE rather than the outcome, so both vocabularies
  // are covered by one function. Byte-identical to Lane 616's mapping for
  // every screen-1 outcome (read->ok->check, failed/unknown->bad->alert,
  // nothing_found/duplicate/blocked->warn->info, reading->busy->spinner).
  const renderUploadIcon = (tone) => {
    if (tone === 'busy') return <div className="hm-upspin" />;
    if (tone === 'ask') {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M9.6 9.4a2.5 2.5 0 1 1 3.2 2.9c-.5.2-.8.7-.8 1.2v.4" />
          <line x1="12" y1="16.7" x2="12" y2="16.7" />
        </svg>
      );
    }
    if (tone === 'ok') {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    }
    if (tone === 'bad') {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" /><line x1="12" y1="8" x2="12" y2="13" /><line x1="12" y1="16.5" x2="12" y2="16.5" />
        </svg>
      );
    }
    // NOTHING_FOUND / DUPLICATE / BLOCKED / PARTIAL / ATTACHED_EMPTY (warn) and
    // NOT_ON_FILE (neutral) — informational, not a failure, and deliberately
    // not a tick. Same glyph either way; only the colour differs.
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" /><line x1="12" y1="11" x2="12" y2="16" /><line x1="12" y1="7.5" x2="12" y2="7.5" />
      </svg>
    );
  };

  const renderUploadCard = (slot) => {
    const { uploadState, attachState, attachBusy } = slot;
    if (!uploadState) return null;
    // Once the buyer has confirmed, the CONFIRM's result is the card. The
    // upload's own headline ("Read X. Which supplier sent it?") would be a
    // stale question after it has been answered.
    const view = attachState || uploadState;
    const tone = UPLOAD_TONE[view.outcome] || 'bad';
    const busy = view.outcome === OUTCOME.READING || attachBusy;
    const candidateId = uploadState.candidateId;
    // ══ SEAM — SCREEN 2 (Lane 625) ══
    // The pipeline parks a classified commercial notice at `awaiting_supplier`
    // having written ZERO assertions; this is the confirm that attributes it.
    // Gated on a real candidate id: without one there is nothing to attach to,
    // and offering a picker that cannot post would promise a recording we
    // cannot make.
    //
    // ══ SEAM — A5a (Lane 676) ══
    // THE SECOND DOOR to that same confirm. Re-uploading a letter whose sender
    // was never confirmed used to DEAD-END: the response short-circuits to
    // `duplicate` before the pipeline runs, so the card reported "Wingman
    // already has this" and offered only a re-read — while the parked row sat
    // there, one pick away from being useful.
    //
    // The prior row's status is what makes this safe, and it is the ONLY thing
    // that does. `priorAwaitingSupplier` is true exclusively for a PARKED
    // notice: zero assertions written, nothing attributed, row still
    // attachable. That is precisely the state `attach-supplier` accepts, so the
    // confirm offered here CANNOT earn the 409 the endpoint raises on a row
    // with active claims — the refusal that made an earlier attempt at this
    // untenable. Every other prior status resolves the flag to false and leaves
    // the card exactly as it shipped (see `docUploadStatus.js` for why
    // `fully_processed` cannot be read as "a supplier is attributed").
    //
    // Deliberately NOT a second route to the deck. Once the buyer confirms,
    // `attachState.supplierId` is a REAL id from a REAL attachment, and the
    // (The Lane 645 chat deck offer that sat below was removed in the Lane 1410 cleanup.) The
    // deck is reached the same way it always was — by establishing the supplier
    // first — which is the only way to reach it truthfully.
    const noticeNeedsSupplier =
      view.outcome === OUTCOME.AWAITING_SUPPLIER ||
      (view.outcome === OUTCOME.DUPLICATE && view.priorAwaitingSupplier === true);
    const showPicker =
      !attachState &&
      noticeNeedsSupplier &&
      candidateId !== null &&
      candidateId !== undefined;
    const orphanedNotice =
      !attachState &&
      noticeNeedsSupplier &&
      (candidateId === null || candidateId === undefined);

    // ══ SEAM — R3-3 (Lane 709): THE CARD NO LONGER OFFERS A MEETING ═════════
    //
    // Ryan, round-3 QA: "I don't like this having it say prepare for a meeting
    // in this little pop up... it should be a button at the bottom of the chat.
    // It comes up not in this pop up modal."
    //
    // Lane 645's gate and Lane 700's per-candidate `supersededByChat` key both
    // lived here and are both GONE, and so is the chat-footer door itself
    // (Lane 1410 cleanup, 2026-10-16: it only bounced back to Home).
    //
    // ⚠️ WHAT LANE 645 AND LANE 700 WERE PROTECTING, AND HOW IT IS STILL
    // PROTECTED — read this before adding any offer back to this card.
    // Lane 700's comment here said, correctly, that suppressing the card on a
    // CAPABILITY FLAG "would strand a buyer whose next question resolved to a
    // different supplier: card offer gone, chat offer withheld, no door left."
    // That risk is real and was MEASURED for this lane rather than argued
    // (`backend/scripts/lane709_chat_deck_offer_withhold_probe.py`): across 17
    // realistic first-questions-after-a-confirm, the server withholds its
    // `deck_offer` on 3/17 (18%) even with every staging dial on, and on 14/17
    // (82%) with NOTICE_CONV_ANCHOR_ENABLED off. So the server-born offer alone
    // is NOT a sufficient replacement, and retiring this card offer against it
    // would have reproduced exactly the failure Lane 645 designed around.
    //
    // The footer door is the answer: it is armed by the CONFIRM (which is
    // server-verified and cannot be withheld by scope resolution) and released
    // by the first assistant reply, so it does not care which supplier the
    // conversation drifted to. (Lane 1410 W3 removed that door; Lane 1410 W4a
    // moved the remaining Meeting Prep doors to `templateDecksEnabled`.)
    return (
      <div key={slot.id} className={`hm-upcard ${tone}`} data-testid="hm-upcard" data-outcome={view.outcome}>
        <div className="hm-upicon">{renderUploadIcon(tone)}</div>
        <div className="hm-uptext">
          <div className="hm-uph">{view.headline}</div>
          {view.detail && <div className="hm-upd">{view.detail}</div>}

          {showPicker && (
            <NoticeSupplierConfirm
              claimsAvailable={uploadState.claimsAvailable}
              authFetch={authFetch}
              busy={attachBusy}
              onConfirm={(supplierId, supplierName) =>
                runAttach(slot.id, candidateId, supplierId, supplierName)}
            />
          )}

          {orphanedNotice && (
            // Read fine, but the response carried no candidate id, so there is
            // no row to attach a supplier to. Say that rather than showing a
            // picker whose Confirm could only fail.
            <div className="hm-nsc-note bad" data-testid="hm-nsc-noid">
              {CONFIRM_COPY.NO_CANDIDATE}
            </div>
          )}

          {/* The Lane 645 deck door stood HERE and was retired by Lane 709 /
              R3-3 — see the seam above `return` for why, and for the
              measurement that says the server-born chat offer could not carry
              the door on its own. The card is back to reporting what happened
              to the document; it no longer proposes what to do next. */}

          {/* After a confirm that attributed nothing (not-on-file, or a write
              that recorded none of its claims), the letter is still attachable
              server-side, so offer the picker back. A completed attach is NOT
              re-pickable — the endpoint 409s on active claims until they are
              retracted, and `canRepick` encodes exactly that. */}
          {attachState && attachState.canRepick && candidateId !== null &&
            candidateId !== undefined && !attachBusy && (
            <div className="hm-uprow">
              <button
                type="button"
                className="hm-upact"
                data-testid="hm-nsc-repick"
                onClick={() => updateSlot(slot.id, { attachState: null })}
              >
                {CONFIRM_COPY.REPICK}
              </button>
            </div>
          )}

          {(view.canRetrySameFile || view.canReupload) && slot.file && !busy && (
            <div className="hm-uprow">
              <button
                type="button"
                className="hm-upact"
                onClick={() => runUpload(slot.id, slot.file, {
                  // A duplicate is the one case needing the endpoint's own
                  // ?retry=true dedup bypass; a failure just needs resending.
                  // `canReupload` is the confirm's 404 — the stored letter is
                  // unreachable, so we want a FRESH candidate, and the parked
                  // row is dedup-eligible (Lane 618), so a plain re-upload
                  // would just come back `duplicate`.
                  retry: view.outcome === OUTCOME.DUPLICATE || !!view.canReupload,
                })}
              >
                {view.retryLabel || (view.canReupload ? CONFIRM_COPY.REUPLOAD : 'Try again')}
              </button>
            </div>
          )}
        </div>
        {!busy && (
          <button
            type="button"
            className="hm-upx"
            aria-label="Dismiss"
            onClick={() => setSlots((prev) => prev.filter((s) => s.id !== slot.id))}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={`hm${homeChatFirstLandingEnabled ? ' hm--chat-first' : ''}${chatMessages.some(msg => msg.role !== 'user' && msg.conversationDeckProposal) ? ' hm--deck-proposal' : ''}`}>

      {/* D.5 — Edit capture modal. position:fixed with z-index 1000, so its
          position in the tree doesn't matter. Mounts only when open. */}
      <CaptureEditModal
        open={editingCandidate !== null}
        candidateId={editingCandidate?.candidateId}
        initialPayload={editingCandidate?.payload}
        onClose={handleEditModalClose}
        onSaveSuccess={handleEditSaveSuccess}
        on409={handleEdit409}
      />

      <div className="hm-body">
        <div className="hm-inner">

          {/* ── LEFT: AI CHAT ── */}
          <div className="hm-chat">
            <div className="hm-threadwrap" ref={scrollRef} tabIndex={0} aria-label="Conversation">
              <div ref={contentRef}>
              {/* Lane 863: the one-time consent banner survives ONLY while
                  `ALWAYS_ON_CAPTURE_ENABLED` is off. The component and
                  `utils/captureConsent.js` are deliberately NOT deleted — both
                  paths have to stay reachable while the flag exists, because
                  flag-OFF must render this page exactly as it renders today.
                  Deleting them is a follow-up for after Ryan has QA'd the ON
                  state on staging and the flag is on in PROD. */}
              {!alwaysOnCaptureEnabled && (
                <ChatCaptureConsentBanner
                  userId={user?.user_id}
                  chatCaptureEnabled={chatCaptureEnabled}
                />
              )}

              {!hasConversation && (
                <div className="hm-empty">
                  <div className="hm-empty-logo"><WingmanLogo size={24} fill="white" /></div>
                  <div className="hm-empty-h">How can I help you today?</div>
                  {homeChatFirstLandingEnabled ? (
                    <>
                      {/* Lane 1211: chat-first guidance — the five ingredients
                          (examples illustrative, not limits), one worked
                          example, and the full-guide link. No quick-action
                          buttons: the chat is the single starting point. */}
                      <div className="hm-recipe-lead">Build your prompt from a few pieces:</div>
                      <div className="hm-rgrid">
                        <div className="hm-rk">Subject</div><div className="hm-rv">a supplier, material, category, or opportunity</div>
                        <div className="hm-rk">Measures</div><div className="hm-rv">spend, delivery, lead time, risk, inventory&hellip;</div>
                        <div className="hm-rk">Breakdown</div><div className="hm-rv">by material, site, month, or category&hellip;</div>
                        <div className="hm-rk">Time period</div><div className="hm-rv">the last 12 months, year to date, exact dates&hellip;</div>
                        <div className="hm-rk">Deliver</div><div className="hm-rv">an answer, talking points, an email, a workbook, or a deck&hellip;</div>
                      </div>
                      <div className="hm-recipe-ex">
                        <div className="hm-ex-tag">Example prompt</div>
                        <div className="hm-ex-body">For [supplier name], analyze spend, delivery, lead time, and problem parts, broken down by material, over the last 12 months. Deliver five talking points for today&rsquo;s call, each with the fact and the ask.</div>
                      </div>
                      <Link className="hm-guide-link" to="/prompt-guide">
                        <span>See the full prompt guide</span>
                        <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h12M11 5l5 5-5 5" /></svg>
                      </Link>
                    </>
                  ) : (
                    <>
                      <div className="hm-empty-sub">Ask about suppliers, materials, contracts, savings, or anything procurement-related.</div>
                      <div className="hm-prompts">
                        {intents.map((intent, idx) => (
                          <button key={idx} className="hm-pchip" onClick={intent.onClick}>
                            {intent.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {sourcingChatIntakeEnabled && !intakeV2 && (
                <SourcingIntakeBanner authFetch={authFetch} conversationId={conversationId}
                  onResumed={onIntakeResumed} />
              )}
              {hasConversation && (
                <div className="hm-thread">
                  {chatMessages.map((msg, idx) => (
                    <div key={msg.id || idx}>
                      {msg.role === 'user' ? (
                        <div className="hm-turn u">
                          <div className="hm-bubble">{msg.content}</div>
                          {msg.id && renderCaptureChip(msg.id)}
                          {msg.id && renderBriefNote(msg.id)}
                        </div>
                      ) : (
                        <div className="hm-ai">
                          <div className="hm-aiav"><WingmanLogo size={14} fill="white" /></div>
                          <div className="hm-aibody">
                            {msg.attachmentStatus && (
                              <div className="hm-attachnote" role="note">{msg.attachmentStatus}</div>
                            )}
                            {msg.answerOrigin === 'mirror'
                              ? (msg.answerLayout
                                ? (
                                  <ChatAnswer
                                    content={msg.content}
                                    layout={msg.answerLayout}
                                    onAsk={(question) => sendChatMessage(question)}
                                    disabled={chatLoading || workbookChoice.locked}
                                  />
                                )
                                : <ChatMarkdown content={msg.content} />)
                              : formatMessage(msg.content)}
                            {/* M2 verification badge: NOT RENDERED — Ryan's
                                ruling, 2026-08-28 (Lane 1107). The hardened
                                scorer proved a ~0% false-positive but ~90%
                                false-negative rate (a fully correct answer
                                scored 3 of 31), so any partial count reads
                                as "mostly wrong" on answers that are right.
                                Scoring persists as internal telemetry only
                                (payload.verification + [MIRROR-BADGE] logs;
                                renderer kept in utils/mirrorVerification).
                                Do not re-render without a new Ryan ruling. */}
                            {/* Lane 690: the honest note. Present only when a
                                document attached to that turn did not reach the
                                answer. It reports the DOCUMENT's state — never
                                what the answer did or did not use, which the
                                client cannot know. */}
                            {msg.docNote && (
                              <div className="hm-docnote">{msg.docNote}</div>
                            )}
                            {msg.conversationDeckProposalOutcome
                              && msg.conversationDeckProposalOutcome.code === SUPPLIER_ASK_CODE && (
                              <ConversationDeckSupplierAsk
                                outcome={msg.conversationDeckProposalOutcome}
                                resolvedName={msg.deckSupplierPicked}
                                disabled={chatLoading}
                                getAuthHeaders={getAuthHeaders}
                                onPick={(name) => pickDeckSupplier(idx, name)}
                              />
                            )}
                            {msg.conversationDeckProposalOutcome
                              && msg.conversationDeckProposalOutcome.code !== SUPPLIER_ASK_CODE
                              && <p className="cdp-error" role="status">
                              {msg.conversationDeckProposalOutcome.message}
                            </p>}
                            {msg.deckBuilder && <DeckBuilderEntry builder={msg.deckBuilder} authFetch={authFetch} conversationId={conversationId} />}
                            {msg.conversationDeckProposal && <ConversationDeckProposalCard
                              proposal={msg.conversationDeckProposal}
                              conversationId={conversationId} messageId={msg.replyToMessageId}
                              authFetch={authFetch}
                            />}
                            {/* Lane 1409: files the chat workbench produced, as download
                                chips (server-written text, authenticated download). */}
                            {Array.isArray(msg.sandboxFiles) && (msg.sandboxFiles.length > 0 || msg.sandboxFilesNote) && (
                              <ChatFileChips files={msg.sandboxFiles} authFetch={authFetch} note={msg.sandboxFilesNote} />
                            )}
                            {/* Lane 1132 (Plan A): the quiet workbook
                                offer, the plan card, and the job row —
                                all owned by components/ReportPlanCard;
                                this page only composes them. */}
                            {workbookExportEnabled && msg.reportOffer
                              && !msg.reportPlan
                              && !msg.reportOfferConsumed && showsAnswerOffer(msg) && (
                              <ReportOfferAction
                                key={msg.reportOffer.token || msg.reportOffer.offer_id}
                                offer={msg.reportOffer}
                                autoStart={msg.reportOfferAutoStart === true}
                                onAutoStarted={() => {
                                  setStartingOffers(prev => [...prev, msg.reportOffer.offer_id]);
                                  setChatMessages(prev => prev.map(m => (
                                    m.reportOffer?.offer_id === msg.reportOffer.offer_id
                                      ? { ...m, reportOfferAutoStart: false } : m)));
                                }}
                                onAutoSettled={() => setStartingOffers(prev => prev.filter(
                                  id => id !== msg.reportOffer.offer_id))}
                                conversationId={conversationId}
                                authFetch={authFetch}
                                getHeaders={getAuthHeaders}
                                onPlanTurn={(data) => {
                                  // The accepted offer is CONSUMED: stamp
                                  // its message so the action never
                                  // resurrects beside the plan card it
                                  // produced (Consult #5), then append
                                  // the plan turn.
                                  // By offer id, not object: the message may have
                                  // been replaced meanwhile (Lane 1405 v3 auto-start).
                                  const offerId = msg.reportOffer.offer_id;
                                  setStartingOffers(prev => prev.filter(id => id !== offerId));
                                  setChatMessages(prev => (
                                    // One plan turn per offer, however many accepts ran.
                                    prev.some(m => m.reportOffer?.offer_id === offerId && m.reportOfferConsumed)
                                      ? prev
                                      : prev
                                        .map(m => (m.reportOffer?.offer_id === offerId
                                          ? { ...m, reportOfferConsumed: true }
                                          : m))
                                        .concat([{
                                          role: 'assistant',
                                          content: data.answer,
                                          answerOrigin: data.answer_origin,
                                          verification: data.verification,
                                          ...projectReportResult(data),
                                        }])));
                                }}
                              />
                            )}
                            {sourcingChatIntakeEnabled && !intakeV2 && msg.sourcingIntakeCard && (
                              <SourcingIntakeCard card={msg.sourcingIntakeCard} conversationId={conversationId} authFetch={authFetch} />
                            )}
                            {intakeV2 && msg.sourcingIntakeCard && (() => {
                              const replaced = newestIntakeCard[msg.sourcingIntakeCard.intake_id] !== idx;
                              return (
                                <SourcingIntakeCardV2 card={msg.sourcingIntakeCard} conversationId={conversationId}
                                  authFetch={authFetch} replaced={replaced} busy={chatLoading}
                                  canChange={idx === liveIntakeIdx}
                                  builtEvent={msg.sourcingIntakeBuiltEvent || null}
                                  onBuilt={(built, out) => setChatMessages((prev) => prev.map((m) => (
                                    m.sourcingIntakeCard && m.sourcingIntakeCard.intake_id === built.intake_id
                                      ? { ...m, sourcingIntakeBuiltEvent: out.event_public_id }
                                      : m)))}
                                  changing={idx === liveIntakeIdx && changeOpen}
                                  onMakeChanges={toggleIntakeChange}
                                  onDiscarded={() => setIntakeChange(null)}
                                  onStartNewChat={() => { setIntakeChange(null); startNewChat(); }} />
                              );
                            })()}
                            {sourcingChatIntakeEnabled && msg.sourcingIntakeBuilt && (
                              <SourcingIntakeBuilt built={msg.sourcingIntakeBuilt} />
                            )}
                            {sourcingEventsEnabled && !sourcingChatIntakeEnabled && msg.sourcingEventOffer && (
                              <SourcingEventOfferAction
                                offer={msg.sourcingEventOffer}
                                conversationId={conversationId}
                                authFetch={authFetch} />
                            )}
                            {workbookExportEnabled && msg.reportStart
                              && !msg.reportJobId && (
                              <ReportStartRetry
                                reportStart={msg.reportStart}
                                authFetch={authFetch}
                                onStarted={(jobId) => {
                                  setChatMessages(prev => prev.map(m => (
                                    m === msg
                                      ? { ...m, reportJobId: jobId }
                                      : m)));
                                }}
                              />
                            )}
                            {workbookExportEnabled && msg.reportPlan
                              && !msg.reportJobId && !msg.reportStart && (
                              <ReportPlanCard
                                plan={msg.reportPlan}
                                authFetch={authFetch}
                                onQueued={(jobId) => {
                                  setChatMessages(prev => prev.map(m => (
                                    m === msg
                                      ? { ...m, reportJobId: jobId }
                                      : m)));
                                }}
                              />
                            )}
                            {msg.reportJobId && (
                              <ReportJobRow jobId={msg.reportJobId}
                                authFetch={authFetch}
                                onReady={workbookChoice.reportReady}
                                onChangeRetry={(job) => {
                                  const draft = workbookChoice.retryChange(job);
                                  if (draft) setChatInput(draft);
                                }} />
                            )}
                            {renderAssistantProvenance(msg, idx)}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="hm-ai">
                      <div className="hm-aiav"><WingmanLogo size={14} fill="white" /></div>
                      {/* Lane 690: while a turn is held for an attached document
                          the bubble says WHICH document and WHAT it is waiting
                          for, and carries the only live control on the screen.
                          `chatWaitLine` non-null is the single source of truth
                          for "a wait is in progress", so the line and the escape
                          can never disagree. */}
                      <div className="hm-aibody">
                        {chatWaitLine ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div className="hm-dots">
                                {[0, 1, 2].map(i => (<div key={i} className="hm-dot" style={{ animationDelay: `${i * 0.16}s` }} />))}
                              </div>
                              <span className="hm-analyzing">{chatWaitLine}</span>
                            </div>
                            {/* Rendered for the WHOLE wait — both phases — and
                                never gated on the picker being up. The composer
                                input and Send are both disabled while a turn is
                                in flight, so this is the buyer's only way out
                                and is load-bearing rather than a nicety. */}
                            <div className="hm-actrow">
                              <button
                                type="button"
                                className="hm-abtn"
                                onClick={() => { skipDocWaitRef.current = true; }}
                              >
                                {SKIP_LABEL}
                              </button>
                            </div>
                          </>
                        ) : (
                          <>
                            <ChatLiveProgress progress={chatProgress} draft={chatStreamDraft} layout={chatStreamLayout} />
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              </div>
            </div>
            {showJump && (
              <button type="button" className="hm-jump-latest" onClick={jumpToLatest}>
                Jump to latest <span aria-hidden="true">↓</span>
              </button>
            )}


            {/* ⛔ NOTHING GOES HERE. Lane 863's capture disclosure stood in this
                slot and Ryan cut it (D9, 2026-08-05): *"I don't want any
                disclosure on the chat page."* The footer holds ONE element —
                the Intelligence link below — and the always-on flag adds
                nothing to it. See the block above `function Home()`. */}

            {/* ══ THE INTELLIGENCE DOOR (Lane 861) ══
                Below the transcript, above the composer — the same footer slot
                as the deck door, because it is the same kind of thing: a way OUT
                of the conversation, offered where the conversation ends.

                PERSISTENT, unlike the chip. It renders in the empty state and
                mid-thread alike, because the question it answers ("where did all
                that go?") is not tied to any one message — which is exactly why
                the chip alone was not enough.

                Gated on `intelligencePortalEnabled` — and the destination is
                `/intelligence` BECAUSE of that same gate, not in spite of it:
                the flag that decides whether this link exists is the flag that
                registers the route it points at, so the two cannot disagree.
                `/intelligence` is also the path Sidebar highlights on, which is
                the teaching half of this change. Read the INTELLIGENCE_COPY
                block at the top of this file before touching either. */}
            {/* Lane 1439 (Ryan, staging 2026-11-03): with the version 2 RFP card the
                link moves BELOW the chat box, so the card's change choices sit right
                above the box with nothing between. Card off: exactly as before. */}
            {intelligencePortalEnabled && !intakeV2 && intelDoor}

            {/* Pinned composer. Lane 616 wraps it in a drop zone and adds an
                attach control + a status card; the form, the input and the
                send button below are UNCHANGED (chat carve-out). */}
            <div
              className="hm-composer hm-upzone"
              onDragOver={docUploadEnabled ? handleUploadDragOver : undefined}
              onDragLeave={docUploadEnabled ? handleUploadDragLeave : undefined}
              onDrop={docUploadEnabled ? handleUploadDrop : undefined}
            >
              {docUploadEnabled && dragActive && (
                <div className="hm-updrop" data-testid="hm-updrop">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 9 12 4 17 9" /><line x1="12" y1="4" x2="12" y2="16" />
                  </svg>
                  Drop the document to read it
                </div>
              )}

              {/* Durable job discovery is user-scoped, not conversation-
                  persistence-scoped. It stays visible after reload and after
                  the creation flag turns off; only new creation controls are
                  gated. */}
              <ReportJobRehydrator authFetch={authFetch}
                knownJobIds={chatMessages
                  .map(m => m.reportJobId).filter(Boolean)} />

              {docUploadEnabled && slots.map((slot) => renderUploadCard(slot))}

              {/* ══ THE STAGING TRAY (A4, Lane 660) ══
                  Attached, NOT read. Nothing here has been sent — there is no
                  candidate row, no request and no status, so it renders as a
                  list of files rather than as a status card. Pressing Send is
                  what turns each of these into a card. */}
              {docUploadEnabled && pending.length > 0 && (
                <div className="hm-uptray" data-testid="hm-uptray">
                  {pending.map((p) => (
                    <span key={p.id} className="hm-uptray-chip" data-testid="hm-uptray-chip">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
                      </svg>
                      <span className="hm-uptray-n">{p.file.name}</span>
                      <button
                        type="button"
                        className="hm-uptray-x"
                        aria-label={`Remove ${p.file.name}`}
                        onClick={() => unstageFile(p.id)}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </span>
                  ))}
                  <span className="hm-uptray-hint" data-testid="hm-uptray-hint">
                    {pending.length === 1
                      ? 'Wingman reads this when you send.'
                      : `Wingman reads these ${pending.length} documents when you send.`}
                  </span>
                </div>
              )}


              {/* Lane 1398 Phase B: after a workbook is built, the buyer
                  says what comes next before typing again. */}
              {workbookChoice.mode === 'choice' && (
                <WorkbookChoiceBoundary onReset={workbookChoice.reset}>
                  <WorkbookChoiceBar onChange={workbookChoice.chooseChange}
                    onOther={workbookChoice.chooseOther} />
                </WorkbookChoiceBoundary>
              )}
              {workbookChoice.editing && (
                <button type="button" className="hm-wbchoice-cancel"
                  data-testid="hm-workbook-change-cancel"
                  onClick={workbookChoice.cancelEdit}>
                  Back to the workbook choices
                </button>
              )}
              <form onSubmit={handleChatSubmit}>
                {/* Lane 1439 (version 2): a draft saved in another chat (not while this chat
                    has its own RFP card: one live draft per chat), and the change box. */}
                {intakeV2 && Object.keys(newestIntakeCard).length === 0 && (
                  <IntakeDraftBar authFetch={authFetch} conversationId={conversationId}
                    onResumed={onIntakeResumed} />
                )}
                {intakeV2 && changeOpen && (
                  <IntakeChangeBar topic={intakeChange.topic}
                    onTopic={(topic) => { setIntakeChange((cur) => (cur ? { ...cur, topic } : cur)); focusComposerEnd(); }}
                    onClose={() => setIntakeChange(null)}
                    onSend={(text) => sendChatMessage(text)}
                    onFill={fillIntakeChange}
                    disabled={chatLoading || workbookChoice.locked} />
                )}
                <div className={intakeV2 && changeOpen ? 'hm-inbar srce2-inbar-on' : 'hm-inbar'}>
                  {docUploadEnabled && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={acceptAttrFor(uploadTablesEnabled)}
                        multiple
                        onChange={handleFilePicked}
                        style={{ display: 'none' }}
                        data-testid="hm-upinput"
                      />
                      {/* No longer disabled while a read is in flight. Attaching
                          forms no request now, and holding a second document
                          hostage to the first one's OCR is precisely the
                          single-file assumption A4 removes. */}
                      <button
                        type="button"
                        className="hm-upbtn"
                        aria-label="Attach a document"
                        title="Attach a supplier letter or document"
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21.44 11.05l-9.19 9.19a5.5 5.5 0 0 1-7.78-7.78l9.19-9.19a3.5 3.5 0 0 1 4.95 4.95l-9.2 9.19a1.5 1.5 0 0 1-2.12-2.12l8.49-8.49" />
                        </svg>
                      </button>
                    </>
                  )}
                  {/* M4B WS4b (Lane 1117): a controlled auto-growing textarea.
                      Enter (no modifiers, not composing) submits exactly once;
                      Shift+Enter inserts a newline; height grows one 20px line
                      at a time to four lines (80px) then scrolls internally,
                      and resets when the input clears (see the effect on
                      chatInput). The attachment-only Send path is untouched. */}
                  <textarea ref={inputRef} rows={1} className="hm-input hm-input-ta" value={chatInput}
                    onChange={(e) => { setChatInput(e.target.value); autogrowComposer(e.target); }}
                    onKeyDown={handleComposerKeyDown}
                    placeholder={workbookChoice.editing
                      ? 'Describe the change to this workbook'
                      : (intakeV2 && changeOpen
                        ? intakeChangePlaceholder(intakeChange.topic)
                        : 'Ask about suppliers, materials, savings, risks...')}
                    disabled={chatLoading || workbookChoice.locked} />
                  {/* A4: a staged document is on its own a reason to Send. Without
                      this, attaching a letter and pressing Send with no typed
                      question would leave the file staged forever — the timing
                      fix would have created a document that never gets read. */}
                  <button type="submit" className="hm-send" aria-label="Send" disabled={chatLoading || workbookChoice.locked || (!chatInput.trim() && pending.length === 0)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                  </button>
                </div>
                {/* Lane 1405 v3: Build a deck / Build a workbook (Ryan's mockup). */}
                <ChatActionButtons
                  deckEnabled={deckBuilderEnabled}
                  workbookEnabled={workbookExportEnabled}
                  conversationId={conversationId}
                  authFetch={authFetch}
                  hasAnswer={chatMessages.some(m => m.role === 'assistant' && m.answerOrigin === 'mirror' && !m.error)}
                  disabled={chatLoading || workbookChoice.locked}
                  workbookStarting={startingOffers.length > 0}
                  onWorkbookOffer={(offer, answerTurnId) => {
                    // The offer is for the answer the server named (its turn id):
                    // that answer's existing workbook action starts at once (the
                    // one-click flow, Lane 1398), wherever it sits in the chat.
                    const { outcome } = placeOfferOnAnswer(chatMessagesRef.current, offer, answerTurnId);
                    if (outcome === 'started') {
                      setChatMessages(prev => placeOfferOnAnswer(prev, offer, answerTurnId).messages);
                    }
                    return outcome;
                  }}
                />
              </form>
            </div>
            {intelligencePortalEnabled && intakeV2 && intelDoor}
          </div>


        </div>
      </div>
    </div>
  );
}

// Lane 1405 v3: the newest completed Mirror answer in the chat, and a key for it
// (its question's message id; its place in the chat when it has none).
// Lane 1439: one Home per chat. "Start a new chat" (the version 2 RFP card) gives a
// new conversation id, and the page starts again exactly as after a reload. With
// that card off the id never changes, so this is the same single Home as before.
function HomeForChat() {
  const { conversationId } = useChatContext();
  return <Home key={conversationId} />;
}

export default HomeForChat;
